// src/admin/ProductManagementPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Search,
  Filter,
  Eye,
  Edit2,
  CheckCircle,
  X,
  RefreshCw,
  Upload,
  Trash2,
  CalendarDays,
  ArrowUpDown,
  SlidersHorizontal,
  Building2,
  Percent,
  PoundSterling,
  ShieldCheck,
  FileText,
  AlertTriangle,
  CheckSquare,
  ChevronDown,
  FileUp,
  Ban,
  Loader2,
  CheckCircle2,
  XCircle,
  FileSpreadsheet,
} from "lucide-react";
import { validateCSVFile } from "../../utils/csvValidator.js";
import { applyListSelection } from "../../utils/rangeSelect.js";
import toast from "react-hot-toast";
import {
  uploadAndImportMortgageProducts,
  uploadAndImportInvoiceFinanceProducts,
} from "../../api/lenderPortal/csvUpload.js";
import {
  getAllMortgageProducts,
  getMortgageProductsDetails,
  updateMortgageProduct,
  deleteMortgageProduct,
  getAllInvoiceFinanceProducts,
  updateInvoiceFinanceProduct,
  deleteInvoiceFinanceProduct,
  isInvoiceFinanceProduct,
} from "../../api/lenderPortal/listedproducts.js";
import { getProductSummaryByType } from "../../api/lenderPortal/mortgageSummary.js";
import { fetchLenderNameByEmail } from "../../api/admin/adminListUsers.js";
import { __productsInternals } from "../../api/lenderPortal/products.js";
import { apiGet, extractMessage } from "../../api/client";
import { TableSkeleton, KpiCardSkeleton } from "../../components/ui/Skeleton.jsx";
import TypeBadge from "../../components/ui/TypeBadge.jsx";
import {
  ViewProductModal,
  EditProductModal,
} from "../lender-portal/ListedProducts/ProductModals.jsx";

/* =============================================================================
   Helpers
============================================================================= */
const money = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && v.startsWith("£")) return v;
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(Number(v));
  } catch {
    return String(v);
  }
};

const pct = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${n.toFixed(2)}%`;
};

const formatRelativeTime = (date) => {
  if (!date) return "—";
  const now = new Date();
  const d = new Date(date);
  const diff = now - d;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-GB");
};

// eslint-disable-next-line no-unused-vars
const formatFullDateTime = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const PRODUCT_LINE_OPTIONS = ["BTL", "IF"];

/** Map Invoice Finance API row → admin Product Management table/edit shape. */
function mapIfToAdminProduct(p, lenderNameByEmail = new Map()) {
  const raw = p._raw || p;
  const id = p._id || raw?.name || "UNKNOWN";
  const sector = p.sector || p.product || raw?.sector || id;
  // IF docs have no lender field; the uploading lender is the doc owner.
  const lenderEmail = String(raw?.lender_name || raw?.lender || raw?.applicant || raw?.owner || "").trim();
  const lender =
    lenderNameByEmail.get(lenderEmail.toLowerCase()) ||
    (lenderEmail.includes("@") ? lenderEmail.split("@")[0] : lenderEmail) ||
    null;

  return {
    ...p,
    id,
    name: sector,
    productName: sector,
    type: "IF",
    lender,
    lenderId: raw?.lender_id || "",
    productType: p.type || p.ifCategory || "—",
    rate: p._discountRateNum ?? 0,
    aprc: null,
    prodFee: p._serviceFeeNum ?? 0,
    productFeeMode: "percent",
    productTerm: p.term || p.contractTerm || "—",
    minLoan: p._minLoan || 0,
    maxLoan: p._maxLoan || 0,
    ltv: null,
    status: p.status || "Active",
    createdAt: raw?.creation ? new Date(raw.creation) : new Date(),
    updatedAt: raw?.modified ? new Date(raw.modified) : new Date(),
    _productLine: "IF",
    _raw: raw,
  };
}

function mapBtlToAdminProduct(p) {
  const raw = p._raw || p;
  return {
    id: p._id || raw?.name || "UNKNOWN",
    name: p.product || raw?.product_name || "Unknown Product",
    productName: p.product || raw?.product_name || "Unknown Product",
    type: p.category || raw?.product_category || "—",
    lender: (() => {
      const lenderEmail = raw?.lender_name || raw?.lender || raw?.applicant || "";
      if (lenderEmail && lenderEmail.includes("@")) {
        return lenderEmail.split("@")[0];
      }
      return lenderEmail || null;
    })(),
    lenderId: raw?.lender_id || "",
    productType: p.type || raw?.product_type || "—",
    rate: p._rateNum || raw?.product_rate_percent || 0,
    aprc: raw?.aprc_percent || raw?.aprc || 0,
    prodFee: raw?.product_fee_amount || 0,
    productFeeMode: p.feeType === "Percentage" ? "percent" : "amount",
    productTerm: raw?.product_termsyears || raw?.product_term_years || raw?.product_term || "0",
    minLoan: p._minLoan || raw?.min_loan || 0,
    maxLoan: p._maxLoan || raw?.max_loan || 0,
    ltv: raw?.ity_percent || raw?.ltv_percent || raw?.ltv || 0,
    status: p.status || raw?.status || "Active",
    mortgageTerm: raw?.mortgage_term_years || raw?.mortgage_term || "",
    purchaseType: raw?.loan_purpose || raw?.purchase_type || "",
    expiryDate: raw?.product_expiray_date || raw?.expiry_date || raw?.product_expiry_date || "",
    borrowerType: raw?.borrower_type || "",
    epcGreen: (() => {
      const epcValue = raw?.epc_rating_green || raw?.epc_green || "";
      if (epcValue === 0 || epcValue === "0") return "0";
      return epcValue || "";
    })(),
    svr: raw?.svr_margin_percent || raw?.svr_margin || raw?.svr || "",
    dscBasic: raw?.basic_taxpayer_dsc_percent || raw?.dsc_basic || raw?.basic_tax_payer_dsc || "",
    dscHigh: raw?.high_taxpayer_dsc || raw?.dsc_high || raw?.high_tax_payer_dsc || "",
    icr: raw?.icr_rate || raw?.icr || "",
    erc1: (() => {
      const val = raw?.year_1 ?? raw?.erc_1 ?? raw?.erc1;
      return val === 0 || val === "0" ? "0" : (val ?? "");
    })(),
    erc2: (() => {
      const val = raw?.year_2 ?? raw?.erc_2 ?? raw?.erc2;
      return val === 0 || val === "0" ? "0" : (val ?? "");
    })(),
    erc3: (() => {
      const val = raw?.year_3 ?? raw?.erc_3 ?? raw?.erc3;
      return val === 0 || val === "0" ? "0" : (val ?? "");
    })(),
    erc4: (() => {
      const val = raw?.year_4 ?? raw?.erc_4 ?? raw?.erc4;
      return val === 0 || val === "0" ? "0" : (val ?? "");
    })(),
    erc5: (() => {
      const val = raw?.year_5 ?? raw?.erc_5 ?? raw?.erc5;
      return val === 0 || val === "0" ? "0" : (val ?? "");
    })(),
    homeOwner: raw?.home_owner || "",
    btlExp: raw?.btl_experince_year || raw?.btl_experience || raw?.btl_exp || "",
    creditWorth: raw?.credit_worthiness || raw?.credit_worth || "",
    tenancyType: raw?.tenancy_type || "",
    tenure: raw?.fh_or_lh || raw?.tenure || raw?.f_h_or_l_h || "",
    leaseYears: raw?.leasehold_remaining_termyears || raw?.lease_years || "",
    exLocal: raw?.ex_local_authority || raw?.ex_local || "",
    depositOrig: raw?.deposit_originantion || raw?.deposit_origination || raw?.deposit_orig || "",
    day1Remo: raw?.day_1_remortgage || raw?.day1_remo || "",
    valuationDoc:
      (raw?.valuation_fee_spreadsheet && !/^\d+$/.test(String(raw.valuation_fee_spreadsheet))
        ? raw.valuation_fee_spreadsheet
        : null) ||
      raw?.valuation_doc ||
      null,
    legalFeesDoc:
      (raw?.leagal_fees_spreadsheet && !/^\d+$/.test(String(raw.leagal_fees_spreadsheet))
        ? raw.leagal_fees_spreadsheet
        : null) ||
      raw?.legal_fees_doc ||
      null,
    valuationFee:
      (raw?.valuation_fee_spreadsheet && /^\d+$/.test(String(raw.valuation_fee_spreadsheet))
        ? Number(raw.valuation_fee_spreadsheet)
        : null) ||
      raw?.valuation_fee ||
      null,
    legalFee:
      (raw?.leagal_fees_spreadsheet && /^\d+$/.test(String(raw.leagal_fees_spreadsheet))
        ? Number(raw.leagal_fees_spreadsheet)
        : null) ||
      raw?.legal_fee ||
      null,
    createdAt: raw?.creation ? new Date(raw.creation) : new Date(),
    updatedAt: raw?.modified ? new Date(raw.modified) : new Date(),
    _productLine: "BTL",
    _raw: raw,
  };
}

async function getDoctypeCount(doctype, filters) {
  const payload = await apiGet("/api/method/frappe.client.get_count", {
    doctype,
    ...(filters ? { filters: JSON.stringify(filters) } : {}),
  });
  return Number(extractMessage(payload) || 0);
}

function parseLoanValue(input) {
  if (!input) return 0;
  let s = String(input).trim().toLowerCase();
  s = s.replace(/£|,/g, "").trim();
  const unitMatch = s.match(/^([\d.]+)\s*([km])$/);
  if (unitMatch) {
    const num = parseFloat(unitMatch[1]);
    const unit = unitMatch[2];
    if (isNaN(num)) return 0;
    if (unit === "k") return Math.round(num * 1000);
    if (unit === "m") return Math.round(num * 1000000);
  }
  const num = parseFloat(s.replace(/[^\d.]/g, ""));
  if (isNaN(num)) return 0;
  return Math.round(num);
}

const allowedSheetExt = /\.(csv|xls|xlsx|ods)$/i;
const MAX_MB = 10;

/* =============================================================================
   Demo Data (replace with backend)
============================================================================= */
const seedProducts = () => [
  {
    id: "BTL-438767234",
    name: "BTL Fixed 3.69% — 2 Year",
    type: "BTL",
    lender: "Paragon Bank",

    // Manual-entry-aligned fields
    productName: "BTL Fixed 3.69% — 2 Year",
    productType: "Fixed",
    lenderCode: "PARA369",
    expiryDate: "2026-01-31",
    purchaseType: "Purchase",
    mortgageTerm: 30,
    productTerm: "2",
    borrowerType: "Ltd Co.",
    epcGreen: "Yes",

    ltv: 75,
    minLoan: 25000,
    maxLoan: 2000000,
    rate: 3.69,
    aprc: 6.1,
    svr: 3.5,

    dscBasic: 125,
    dscHigh: 145,
    icr: 125,

    prodFee: 995,
    productFeeMode: "pound",
    appFee: 0,

    erc1: 5,
    erc2: 4,
    erc3: 3,
    erc4: 2,
    erc5: 1,

    homeOwner: "Yes",
    btlExp: "1-5",
    creditWorth: "Excellent 881+",
    tenancyType: "AST",
    tenure: "FREEHOLD",
    leaseYears: "",
    exLocal: "No",
    depositOrig: "Savings",
    day1Remo: "Market Value",

    cashback: 0,
    features: {
      Portable: false,
      Overpayments: true,
      Underpayments: false,
      "Payment Holiday": false,
      "Free Legals": true,
      "Free Valuation": false,
    },

    scheduleEnabled: false,
    scheduleAt: "",
    valuationDoc: null,
    legalFeesDoc: null,

    status: "Active", // Active | Deactivated | Pending | Scheduled
    scheduledAt: null,
    createdAt: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
    createdBy: "Admin",
    lastEditedBy: "Admin",
    notes: 2,
  },
  {
    id: "AF-542189073",
    name: "Asset Finance 4.05% — 36 Month",
    type: "AF",
    lender: "Shawbrook Bank",
    productName: "Asset Finance 4.05% — 36 Month",
    productType: "Fixed",
    lenderCode: "SHAW405",
    expiryDate: "2026-02-28",
    purchaseType: "Purchase",
    mortgageTerm: 0,
    productTerm: "3",
    borrowerType: "Ltd Co.",
    epcGreen: "No",
    ltv: 0,
    minLoan: 10000,
    maxLoan: 500000,
    rate: 4.05,
    aprc: 0,
    svr: 0,
    dscBasic: 100,
    dscHigh: 100,
    icr: 1,
    prodFee: 299,
    productFeeMode: "pound",
    appFee: 299,
    erc1: 0,
    erc2: 0,
    erc3: 0,
    erc4: 0,
    erc5: 0,
    homeOwner: "Any",
    btlExp: "0",
    creditWorth: "Fair 721-880",
    tenancyType: "None",
    tenure: "FREEHOLD",
    leaseYears: "",
    exLocal: "No",
    depositOrig: "Savings",
    day1Remo: "Market Value",
    cashback: 0,
    features: {
      Portable: false,
      Overpayments: false,
      Underpayments: false,
      "Payment Holiday": false,
      "Free Legals": false,
      "Free Valuation": false,
    },
    scheduleEnabled: false,
    scheduleAt: "",
    valuationDoc: null,
    legalFeesDoc: null,
    status: "Deactivated",
    scheduledAt: null,
    createdAt: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000),
    createdBy: "Admin",
    lastEditedBy: "Admin",
    notes: 1,
  },
  {
    id: "INV-657294813",
    name: "Invoice Finance 3.20% — 12 Month",
    type: "IF",
    lender: "Funding Circle",
    productName: "Invoice Finance 3.20% — 12 Month",
    productType: "Variable",
    lenderCode: "FC320",
    expiryDate: "2026-03-15",
    purchaseType: "Purchase",
    mortgageTerm: 0,
    productTerm: "1",
    borrowerType: "Ltd Co.",
    epcGreen: "No",
    ltv: 0,
    minLoan: 25000,
    maxLoan: 750000,
    rate: 3.2,
    aprc: 0,
    svr: 0,
    dscBasic: 100,
    dscHigh: 100,
    icr: 1,
    prodFee: 1.5,
    productFeeMode: "percent",
    appFee: 0,
    erc1: 0,
    erc2: 0,
    erc3: 0,
    erc4: 0,
    erc5: 0,
    homeOwner: "Any",
    btlExp: "0",
    creditWorth: "Excellent 881+",
    tenancyType: "None",
    tenure: "FREEHOLD",
    leaseYears: "",
    exLocal: "No",
    depositOrig: "Savings",
    day1Remo: "Market Value",
    cashback: 0,
    features: {
      Portable: false,
      Overpayments: false,
      Underpayments: false,
      "Payment Holiday": false,
      "Free Legals": false,
      "Free Valuation": false,
    },
    scheduleEnabled: true,
    scheduleAt: "",
    valuationDoc: null,
    legalFeesDoc: null,
    status: "Scheduled",
    scheduledAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
    createdAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 3 * 60 * 60 * 1000),
    createdBy: "Admin",
    lastEditedBy: "Admin",
    notes: 0,
  },
];

/* =============================================================================
   Lender Search Input Component
============================================================================= */
function LenderSearchInput({ value, onChange, disabled, lenders = [], products = [], placeholder }) {
  const [searchTerm, setSearchTerm] = useState(value || "");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const inputRef = useRef(null);

  // Build searchable lender options from products
  const lenderOptions = useMemo(() => {
    const options = new Map();
    products.forEach((p) => {
      if (p.lender) {
        if (!options.has(p.lender)) {
          options.set(p.lender, {
            name: p.lender,
            id: p.lenderId || "",
          });
        }
      }
      if (p.lenderId && p.lenderId !== p.lender) {
        if (!options.has(p.lenderId)) {
          options.set(p.lenderId, {
            name: p.lender || p.lenderId,
            id: p.lenderId,
          });
        }
      }
    });
    // Add lenders from the lenders list
    lenders.forEach((l) => {
      if (!options.has(l)) {
        options.set(l, { name: l, id: "" });
      }
    });
    return Array.from(options.values());
  }, [products, lenders]);

  // Filter suggestions based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSuggestions([]);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = lenderOptions.filter((option) => {
      const nameMatch = option.name?.toLowerCase().includes(term);
      const idMatch = option.id?.toLowerCase().includes(term);
      return nameMatch || idMatch;
    }).slice(0, 10); // Limit to 10 suggestions

    setSuggestions(filtered);
  }, [searchTerm, lenderOptions]);

  const handleInputChange = (v) => {
    setSearchTerm(v);
    setShowSuggestions(true);
    // If user clears, clear both fields
    if (!v) {
      onChange("", "");
    } else {
      // Allow manual entry - update lender name
      onChange(v, "");
    }
  };

  const handleSelect = (option) => {
    setSearchTerm(option.name || option.id);
    setShowSuggestions(false);
    onChange(option.name || "", option.id || "");
  };

  const handleBlur = () => {
    // Delay to allow click on suggestion
    setTimeout(() => setShowSuggestions(false), 200);
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          data-key="lenderSearch"
          value={searchTerm}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400 disabled:bg-slate-100"
        />
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((option, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleSelect(option)}
              className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm text-slate-900 first:rounded-t-xl last:rounded-b-xl"
            >
              <div className="font-medium">{option.name || option.id}</div>
              {option.id && option.id !== option.name && (
                <div className="text-xs text-slate-500">ID: {option.id}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* =============================================================================
   UI Bits
============================================================================= */
function StatusPill({ status }) {
  const base =
    "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border";
  const map = {
    Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Deactivated: "bg-red-50 text-red-700 border-red-200",
    Pending: "bg-amber-50 text-amber-700 border-amber-200",
    Scheduled: "bg-sky-50 text-sky-700 border-sky-200",
  };
  return (
    <span className={`${base} ${map[status] || map.Pending}`}>{status}</span>
  );
}

function KpiCard({ title, value, icon: Icon, tone = "slate", hint, onClick }) {
  const toneMap = {
    slate: "border-slate-200 hover:border-slate-300",
    sky: "border-sky-200 hover:border-sky-300 bg-sky-50/30",
    emerald: "border-emerald-200 hover:border-emerald-300 bg-emerald-50/30",
    red: "border-red-200 hover:border-red-300 bg-red-50/30",
    amber: "border-amber-200 hover:border-amber-300 bg-amber-50/30",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left bg-white rounded-2xl border p-4 transition hover:shadow-md active:scale-[0.99] ${toneMap[tone]}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium text-slate-500">{title}</div>
        {Icon ? <Icon className="h-4 w-4 text-slate-400" /> : null}
      </div>
      <div className="text-2xl font-semibold text-slate-900">{value}</div>
      {hint ? <div className="text-xs text-slate-500 mt-1">{hint}</div> : null}
    </button>
  );
}

function Dropdown({ label, value, onChange, options, icon: Icon, allowAll = true, optionLabels }) {
  const [open, setOpen] = useState(false);
  const displayValue = value
    ? (optionLabels && optionLabels[value]) || value
    : null;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={`flex items-center gap-1.5 px-2.5 py-2 text-xs font-medium rounded-lg border transition ${
          value
            ? "bg-sky-50 text-sky-700 border-sky-200"
            : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
        }`}
      >
        {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" /> : null}
        <span className="whitespace-nowrap">{label}</span>
        {displayValue ? (
          <span className="px-1.5 py-0.5 bg-sky-100 text-sky-700 rounded-md whitespace-nowrap max-w-[7rem] truncate">
            {displayValue}
          </span>
        ) : null}
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 transition ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open ? (
        <div className="absolute z-20 mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {allowAll ? (
            <button
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-50 transition ${
                !value
                  ? "bg-slate-50 text-slate-900 font-medium"
                  : "text-slate-700"
              }`}
            >
              All
            </button>
          ) : null}

          {options.map((opt, idx) => {
            const optValue = String(opt ?? "").trim();
            if (!optValue) return null;
            return (
            <button
              key={`${label}-${optValue}-${idx}`}
              onClick={() => {
                onChange(optValue);
                setOpen(false);
              }}
              className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-50 transition ${
                value === optValue
                  ? "bg-sky-50 text-sky-700 font-medium"
                  : "text-slate-700"
              }`}
            >
              {(optionLabels && optionLabels[optValue]) || optValue}
            </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function ConfirmModal({
  title,
  description,
  confirmText,
  tone = "red",
  onClose,
  onConfirm,
}) {
  const toneBtn =
    tone === "red"
      ? "bg-red-600 hover:bg-red-700"
      : tone === "emerald"
      ? "bg-emerald-600 hover:bg-emerald-700"
      : "bg-sky-600 hover:bg-sky-700";
  const toneIcon =
    tone === "red"
      ? "bg-red-100 text-red-700"
      : tone === "emerald"
      ? "bg-emerald-100 text-emerald-700"
      : "bg-sky-100 text-sky-700";

  // Use CheckCircle for success (emerald), AlertTriangle for warnings/errors
  const Icon = tone === "emerald" ? CheckCircle : AlertTriangle;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`h-9 w-9 rounded-xl flex items-center justify-center ${toneIcon}`}
            >
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-base font-semibold text-slate-900">
                {title}
              </div>
              <div className="text-xs text-slate-500">{description}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition"
          >
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>
        <div className="p-6 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition ${toneBtn}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function Pagination({ currentPage, totalPages, itemsPerPage, totalCount, onPage, onItemsPerPageChange, pageSizeOptions, allPageSize }) {
  const displayPerPage = itemsPerPage >= (allPageSize || 1000000) ? "All" : itemsPerPage;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 px-6 py-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="text-sm text-slate-600">
          Page {currentPage} of {totalPages}
          {totalCount != null && (
            <span className="ml-2">({totalCount} products)</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {totalPages > 1 && (
            <>
              <button
                onClick={() => onPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                onClick={() => onPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous"
              >
                <ArrowUpDown className="h-4 w-4 rotate-90" />
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (p) =>
                      p === 1 ||
                      p === totalPages ||
                      (p >= currentPage - 2 && p <= currentPage + 2)
                  )
                  .map((p, idx, arr) => {
                    const prev = arr[idx - 1];
                    const showDots = prev && p - prev > 1;
                    return (
                      <React.Fragment key={p}>
                        {showDots ? (
                          <span className="px-2 text-slate-400">.</span>
                        ) : null}
                        <button
                          onClick={() => onPage(p)}
                          className={`min-w-[2.5rem] px-3 py-1.5 text-sm font-medium rounded-lg transition ${
                            currentPage === p
                              ? "bg-sky-500 text-white"
                              : "text-slate-700 bg-white border border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>
              <button
                onClick={() => onPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next"
              >
                <ArrowUpDown className="h-4 w-4 -rotate-90" />
              </button>
              <button
                onClick={() => onPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </>
          )}

          {onItemsPerPageChange && Array.isArray(pageSizeOptions) && (
            <div className="flex items-center gap-2">
              <label htmlFor="items-per-page" className="text-sm text-slate-600 whitespace-nowrap">
                Per page:
              </label>
              <select
                id="items-per-page"
                value={itemsPerPage >= (allPageSize || 1000000) ? "all" : itemsPerPage}
                onChange={(e) => onItemsPerPageChange(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700 focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
              >
                {pageSizeOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
                <option value="all">All</option>
              </select>
            </div>
          )}

          {!onItemsPerPageChange && (
            <div className="text-sm text-slate-600">{displayPerPage} per page</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =============================================================================
   Shared Form Components
============================================================================= */
function Section({ title, icon: Icon, children, hint }) {
  return (
    <div className="rounded-2xl border border-sky-200 bg-sky-50/30 p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          {Icon ? (
            <span className="h-9 w-9 rounded-xl bg-white border border-sky-200 flex items-center justify-center">
              <Icon className="h-5 w-5 text-sky-700" />
            </span>
          ) : null}
          <div>
            <div className="text-base font-semibold text-slate-900">
              {title}
            </div>
            {hint ? (
              <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
            ) : null}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <label className="block">
      <div className="mb-1 text-sm font-medium text-slate-700">
        {label} {required ? <span className="text-rose-600">*</span> : null}
      </div>
      {children}
      {error ? <p className="mt-1 text-xs text-rose-600">{error}</p> : null}
    </label>
  );
}

function Input({
  value,
  onChange,
  disabled,
  placeholder,
  type = "text",
  dataKey,
  min,
  max,
  step,
  isView = false,
}) {
  // In view mode, show plain text without input styling
  if (isView) {
    const displayValue = value !== null && value !== undefined && value !== "" ? String(value) : "—";
    return (
      <div className="w-full px-3 py-2 text-sm text-slate-900 bg-slate-50 rounded-lg border border-slate-200">
        {displayValue}
      </div>
    );
  }
  
  return (
    <input
      data-key={dataKey}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      type={type}
      min={min}
      max={max}
      step={step}
      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400 disabled:bg-slate-100"
    />
  );
}

function Select({ value, onChange, disabled, options, isView = false }) {
  // In view mode, show plain text without dropdown arrow
  if (isView) {
    // Find the label for the current value
    const option = options.find(o => (o.value ?? o) === value);
    const displayValue = option?.label ?? option?.value ?? value ?? "—";
    return (
      <div className="w-full px-3 py-2 text-sm text-slate-900 bg-slate-50 rounded-lg border border-slate-200">
        {displayValue}
      </div>
    );
  }
  
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400 disabled:bg-slate-100 bg-white"
    >
      {options.map((o) => (
        <option key={o.value ?? o} value={o.value ?? o}>
          {o.label ?? o}
        </option>
      ))}
    </select>
  );
}

function TogglePills({ value, onChange, disabled, options }) {
  return (
    <div
      className={`inline-flex rounded-full bg-slate-100 p-1 text-[11px] font-medium ${
        disabled ? "opacity-60" : ""
      }`}
    >
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.id)}
            className={`px-2.5 py-0.5 rounded-full transition-colors ${
              active
                ? "bg-white text-sky-700 shadow-sm"
                : "text-slate-600 hover:text-slate-800"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function FileCard({ title, subtitle, file, onPick, onClear, disabled }) {
  const sizeMB = file ? (file.size / 1024 / 1024).toFixed(2) : null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <div className="text-xs text-slate-500">{subtitle}</div>
        </div>
        {file ? (
          <span className="text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-600">
            {sizeMB} MB
          </span>
        ) : null}
      </div>

      {!file ? (
        <button
          type="button"
          disabled={disabled}
          onClick={onPick}
          className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition ${
            disabled
              ? "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"
              : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
          }`}
        >
          <FileUp className="h-4 w-4" />
          Choose file
        </button>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate">
              {file.name}
            </div>
            <div className="text-xs text-slate-500">Ready</div>
          </div>
          <button
            type="button"
            onClick={onClear}
            className="p-2 rounded-lg border border-slate-200 hover:bg-rose-50 hover:border-rose-200 transition"
            title="Remove"
          >
            <X className="h-4 w-4 text-rose-600" />
          </button>
        </div>
      )}
    </div>
  );
}

/* =============================================================================
   Product Form Model (matches your ManualEntryPage fields)
============================================================================= */
const blankProductForm = () => ({
  // Basic
  productName: "",
  productType: "",
  lender: "",
  lenderId: "",
  expiryDate: "",
  purchaseType: "",
  mortgageTerm: "",
  productTerm: "",
  borrowerType: "",
  epcGreen: "",

  // Mortgage numbers
  ltv: "",
  minLoan: "",
  maxLoan: "",
  rate: "",
  aprc: "",
  svr: "",

  // Coverage
  dscBasic: "",
  dscHigh: "",
  icr: "",

  // Fees
  prodFee: "",
  productFeeMode: "pound", // pound | percent | min
  appFee: "",

  // ERCs
  erc1: "",
  erc2: "",
  erc3: "",
  erc4: "",
  erc5: "",

  // Property requirements
  homeOwner: "",
  btlExp: "",
  creditWorth: "",
  tenancyType: "",
  tenure: "",
  leaseYears: "",
  exLocal: "",
  depositOrig: "",
  day1Remo: "",

  // Features
  cashback: "",
  features: {
    Portable: false,
    Overpayments: false,
    Underpayments: false,
    "Payment Holiday": false,
    "Free Legals": false,
    "Free Valuation": false,
  },

  // Scheduling + docs
  scheduleEnabled: false,
  scheduleAt: "",
  valuationDoc: null,
  legalFeesDoc: null,

  // Admin-only
  status: "Pending",
  notes: 0,
});

const TEMPLATE_FIELDS = [
  // Basic
  "productName",
  "productType",
  "lender",
  "lenderId",
  "expiryDate",
  "purchaseType",
  "mortgageTerm",
  "productTerm",
  "borrowerType",
  "epcGreen",

  // Mortgage numbers
  "ltv",
  "minLoan",
  "maxLoan",
  "rate",
  "aprc",
  "svr",

  // Coverage
  "dscBasic",
  "dscHigh",
  "icr",

  // Fees
  "prodFee",
  "productFeeMode",
  "appFee",

  // ERCs
  "erc1",
  "erc2",
  "erc3",
  "erc4",
  "erc5",

  // Property requirements
  "homeOwner",
  "btlExp",
  "creditWorth",
  "tenancyType",
  "tenure",
  "leaseYears",
  "exLocal",
  "depositOrig",
  "day1Remo",

  // Features
  "cashback",
  "feature_Portable",
  "feature_Overpayments",
  "feature_Underpayments",
  "feature_PaymentHoliday",
  "feature_FreeLegals",
  "feature_FreeValuation",

  // Scheduling
  "scheduleEnabled",
  "scheduleAt",

  // Admin
  "status",
  "notes",
];

/* =============================================================================
   Product Modal (Create/Edit/View) — includes ALL fields
============================================================================= */
function ProductModal({ mode, product, onClose, onSave, lenders = [], products = [] }) {
  const isView = mode === "view";

  const [tab, setTab] = useState("basic");
  const [form, setForm] = useState(() => {
    const base = blankProductForm();
    if (!product) return base;

    // Use product data directly, with fallback to _raw backend data
    const raw = product._raw || {};
    return {
      ...base,
      productName: product.productName ?? product.name ?? raw?.product_name ?? "",
      productType: product.productType ?? raw?.product_type ?? "",
      lender: product.lender ?? raw?.lender_name ?? raw?.lender ?? "",
      lenderId: product.lenderId ?? raw?.lender_id ?? "",
      expiryDate: product.expiryDate ?? raw?.product_expiray_date ?? raw?.expiry_date ?? raw?.product_expiry_date ?? "",
      purchaseType: product.purchaseType ?? raw?.loan_purpose ?? raw?.purchase_type ?? "",
      mortgageTerm: product.mortgageTerm ?? raw?.mortgage_term_years ?? raw?.mortgage_term ?? "",
      productTerm: product.productTerm ?? raw?.product_termsyears ?? raw?.product_term_years ?? raw?.product_term ?? "",
      borrowerType: product.borrowerType ?? raw?.borrower_type ?? "",
      epcGreen: (() => {
        const epcValue = product.epcGreen ?? raw?.epc_rating_green ?? raw?.epc_green ?? "";
        // Show "0" if value is 0, otherwise use the value or empty string
        if (epcValue === 0 || epcValue === "0") return "0";
        return epcValue || "";
      })(),

      ltv: product.ltv ?? raw?.ity_percent ?? raw?.ltv_percent ?? raw?.ltv ?? "",
      minLoan: product.minLoan ?? raw?.min_loan ?? "",
      maxLoan: product.maxLoan ?? raw?.max_loan ?? "",
      rate: product.rate ?? raw?.product_rate_percent ?? "",
      aprc: product.aprc ?? raw?.aprc_percent ?? raw?.aprc ?? "",
      svr: product.svr ?? raw?.svr_margin_percent ?? raw?.svr_margin ?? raw?.svr ?? "",

      dscBasic: product.dscBasic ?? raw?.basic_taxpayer_dsc_percent ?? raw?.dsc_basic ?? raw?.basic_tax_payer_dsc ?? "",
      dscHigh: product.dscHigh ?? raw?.high_taxpayer_dsc ?? raw?.dsc_high ?? raw?.high_tax_payer_dsc ?? "",
      icr: product.icr ?? raw?.icr_rate ?? raw?.icr ?? "",

      prodFee: product.prodFee ?? raw?.product_fee_amount ?? "",
      productFeeMode: product.productFeeMode ?? (raw?.product_fee_type === "Percentage" ? "percent" : "pound"),
      appFee: product.appFee ?? raw?.application_fee ?? "",

      erc1: (() => {
        const val = product.erc1 ?? raw?.year_1 ?? raw?.erc_1 ?? raw?.erc1;
        return val === 0 || val === "0" ? "0" : (val ?? "");
      })(),
      erc2: (() => {
        const val = product.erc2 ?? raw?.year_2 ?? raw?.erc_2 ?? raw?.erc2;
        return val === 0 || val === "0" ? "0" : (val ?? "");
      })(),
      erc3: (() => {
        const val = product.erc3 ?? raw?.year_3 ?? raw?.erc_3 ?? raw?.erc3;
        return val === 0 || val === "0" ? "0" : (val ?? "");
      })(),
      erc4: (() => {
        const val = product.erc4 ?? raw?.year_4 ?? raw?.erc_4 ?? raw?.erc4;
        return val === 0 || val === "0" ? "0" : (val ?? "");
      })(),
      erc5: (() => {
        const val = product.erc5 ?? raw?.year_5 ?? raw?.erc_5 ?? raw?.erc5;
        return val === 0 || val === "0" ? "0" : (val ?? "");
      })(),

      homeOwner: product.homeOwner ?? raw?.home_owner ?? "",
      btlExp: product.btlExp ?? raw?.btl_experince_year ?? raw?.btl_experience ?? raw?.btl_exp ?? "",
      creditWorth: product.creditWorth ?? raw?.credit_worthiness ?? raw?.credit_worth ?? "",
      tenancyType: product.tenancyType ?? raw?.tenancy_type ?? "",
      tenure: product.tenure ?? raw?.fh_or_lh ?? raw?.tenure ?? raw?.f_h_or_l_h ?? "",
      leaseYears: product.leaseYears ?? raw?.leasehold_remaining_termyears ?? raw?.lease_years ?? "",
      exLocal: product.exLocal ?? raw?.ex_local_authority ?? raw?.ex_local ?? "",
      depositOrig: product.depositOrig ?? raw?.deposit_originantion ?? raw?.deposit_origination ?? raw?.deposit_orig ?? "",
      day1Remo: product.day1Remo ?? raw?.day_1_remortgage ?? raw?.day1_remo ?? "",

      cashback: product.cashback ?? raw?.cashback ?? "",
      features: {
        ...base.features,
        ...(product.features || {}),
        // Map API boolean fields (0/1) to feature checkboxes
        Portable: raw?.portable === 1 || raw?.portable === true || product.features?.Portable || false,
        Overpayments: raw?.overpayents === 1 || raw?.overpayents === true || raw?.overpayments === 1 || raw?.overpayments === true || product.features?.Overpayments || false,
        Underpayments: raw?.underpayments === 1 || raw?.underpayments === true || product.features?.Underpayments || false,
        "Free Legals": raw?.free_legals === 1 || raw?.free_legals === true || product.features?.["Free Legals"] || false,
        "Free Valuation": raw?.free_valuation === 1 || raw?.free_valuation === true || product.features?.["Free Valuation"] || false,
        "Payment Holiday": raw?.payment_holiday === 1 || raw?.payment_holiday === true || product.features?.["Payment Holiday"] || false,
      },

      scheduleEnabled: product.scheduleEnabled ?? (raw?.product_schedule === 1) ?? false,
      scheduleAt: product.scheduleAt ?? raw?.date_time ?? raw?.schedule_at ?? "",
      // Handle valuation and legal fees - can be file URLs or numeric values (as strings)
      valuationDoc: product.valuationDoc ?? (raw?.valuation_fee_spreadsheet && !/^\d+$/.test(String(raw.valuation_fee_spreadsheet)) ? raw.valuation_fee_spreadsheet : null) ?? raw?.valuation_doc ?? null,
      legalFeesDoc: product.legalFeesDoc ?? (raw?.leagal_fees_spreadsheet && !/^\d+$/.test(String(raw.leagal_fees_spreadsheet)) ? raw.leagal_fees_spreadsheet : null) ?? raw?.legal_fees_doc ?? null,
      valuationFee: product.valuationFee ?? (raw?.valuation_fee_spreadsheet && /^\d+$/.test(String(raw.valuation_fee_spreadsheet)) ? Number(raw.valuation_fee_spreadsheet) : null) ?? raw?.valuation_fee ?? null,
      legalFee: product.legalFee ?? (raw?.leagal_fees_spreadsheet && /^\d+$/.test(String(raw.leagal_fees_spreadsheet)) ? Number(raw.leagal_fees_spreadsheet) : null) ?? raw?.legal_fee ?? null,
    };
  });

  const [errors, setErrors] = useState({});
  const fileValRef = useRef(null);
  const fileLegalRef = useRef(null);

  const set = (k, v) => {
    setForm((s) => {
      const next = { ...s, [k]: v };
      if (k === "tenure" && v !== "LEASEHOLD") next.leaseYears = "";
      if (k === "features") next.features = v;
      return next;
    });
  };

  const validate = (f) => {
    const e = {};
    // Only validate truly required fields for updates
    const req = [
      "productName",
      "productType",
    ];

    for (const k of req) {
      if (String(f[k] ?? "").trim() === "") e[k] = "Required";
    }
    if (f.tenure === "LEASEHOLD" && String(f.leaseYears ?? "").trim() === "")
      e.leaseYears = "Required";

    if (f.scheduleEnabled && !f.scheduleAt)
      e.scheduleAt = "Choose a schedule date/time";

    // Validation for valuation and legal fees removed
    // These can be either file objects (for new uploads) or URLs/numeric values (from backend)
    // Only validate file format if a new file is being uploaded
    if (!f.features?.["Free Valuation"]) {
      // Only validate if it's a new file object being uploaded
      if (f.valuationDoc && typeof f.valuationDoc === 'object' && f.valuationDoc instanceof File) {
        if (!allowedSheetExt.test(f.valuationDoc.name))
          e.valuationDoc = "Upload .csv/.xls/.xlsx/.ods";
        else if (f.valuationDoc.size > MAX_MB * 1024 * 1024)
          e.valuationDoc = `File > ${MAX_MB}MB`;
      }
      // If it's a URL or numeric value from backend, no validation needed
    }

    // Only validate if it's a new file object being uploaded
    if (f.legalFeesDoc && typeof f.legalFeesDoc === 'object' && f.legalFeesDoc instanceof File) {
      if (!allowedSheetExt.test(f.legalFeesDoc.name))
        e.legalFeesDoc = "Upload .csv/.xls/.xlsx/.ods";
      else if (f.legalFeesDoc.size > MAX_MB * 1024 * 1024)
        e.legalFeesDoc = `File > ${MAX_MB}MB`;
    }
    // If it's a URL or numeric value from backend, no validation needed

    const toN = (x) => Number(x);
    const nonNeg = [
      "ltv",
      "minLoan",
      "maxLoan",
      "rate",
      "aprc",
      "svr",
      "prodFee",
      "appFee",
      "cashback",
    ];
    nonNeg.forEach((k) => {
      if (String(f[k] ?? "").trim() === "") return;
      const n = toN(f[k]);
      if (Number.isNaN(n)) e[k] = "Must be a number";
      else if (n < 0) e[k] = "Cannot be negative";
    });

    if (String(f.maxLoan ?? "").trim() && String(f.minLoan ?? "").trim()) {
      const a = toN(f.minLoan);
      const b = toN(f.maxLoan);
      if (!Number.isNaN(a) && !Number.isNaN(b) && b < a)
        e.maxLoan = "Max Loan must be ≥ Min Loan";
    }

    return e;
  };

  // Remove schedule tab in view mode; Features tab removed — manage type-by-type only
  const tabs = [
    { id: "basic", label: "Basic", icon: FileText },
    { id: "mortgage", label: "Mortgage", icon: Percent },
    { id: "coverage", label: "Coverage", icon: ShieldCheck },
    { id: "fees", label: "Fees & ERCs", icon: PoundSterling },
    { id: "property", label: "Property", icon: Building2 },
    { id: "docs", label: "Docs", icon: FileUp },
    ...(isView ? [] : [{ id: "schedule", label: "Schedule", icon: CalendarDays }]),
  ];

  const submit = async () => {
    console.log("🔵 Submit button clicked");
    console.log("Form data:", form);
    
    const e = validate(form);
    console.log("Validation errors:", e);
    setErrors(e);
    
    if (Object.keys(e).length) {
      console.log("❌ Validation failed, errors:", e);
      const first = Object.keys(e)[0];
      const node = document.querySelector(`[data-key="${first}"]`);
      node?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      node?.focus?.();
      return;
    }
    
    console.log("✅ Validation passed, proceeding with save...");

    const id =
      product?.id ||
      `${(form.productType || "PRD").toUpperCase().slice(0, 3)}-${String(
        Math.floor(Math.random() * 900000000 + 100000000)
      )}`;

    const out = {
      ...(product || {}),
      id,
      name: form.productName,
      type: product?.type || "BTL",
      lender: form.lender,
      lenderId: form.lenderId,
      rate: Number(form.rate),

      ...form,

      // Preserve _raw from original product for API update
      _raw: product?._raw || product || {},

      updatedAt: new Date(),
      lastEditedBy: "Admin",
      scheduledAt:
        form.status === "Scheduled" && form.scheduleAt
          ? new Date(form.scheduleAt)
          : product?.scheduledAt ?? null,
    };

    try {
      console.log("Submitting product update:", out);
      const result = await onSave(out);
      
      // If update was successful, the confirmation modal will be shown by parent component
      // Only close if no confirmation is being shown
      if (!result?.success) {
        onClose();
      }
      // If result.success is true, parent will show confirmation modal and close this modal
    } catch (error) {
      console.error("Error in submit:", error);
      // Error is already handled in upsertProduct, but don't close modal on error
      // The error toast will be shown by upsertProduct
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-sky-50 border border-sky-200 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-sky-700" />
            </div>
            <div>
              <div className="text-base font-semibold text-slate-900">
                {mode === "create"
                  ? "Add Product"
                  : mode === "edit"
                  ? "Edit Product"
                  : "Product Details"}
              </div>
              <div className="text-xs text-slate-500">
                {product?.id ? `${product.id} • ` : ""}
                Last updated{" "}
                {formatRelativeTime(
                  product?.updatedAt || product?.createdAt || new Date()
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition"
              title="Close"
            >
              <X className="h-5 w-5 text-slate-500" />
            </button>
          </div>
        </div>

        <div className="px-6 border-b border-slate-200 flex gap-1 overflow-x-auto">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                  tab === t.id
                    ? "border-sky-500 text-sky-600"
                    : "border-transparent text-slate-600 hover:text-slate-900"
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {tab === "basic" ? (
            <Section title="Basic Product Information" icon={FileText}>
              <Field label="Product Name" required error={errors.productName}>
                <Input
                  dataKey="productName"
                  value={form.productName}
                  onChange={(v) => set("productName", v)}
                  disabled={isView}
                  isView={isView}
                  placeholder="e.g. 5-Year Fixed Mortgage"
                />
              </Field>

              <Field label="Product Type" required error={errors.productType}>
                <Select
                  value={form.productType}
                  onChange={(v) => set("productType", v)}
                  disabled={isView}
                  isView={isView}
                  options={[
                    { value: "", label: "Select" },
                    "Fixed",
                    "Variable",
                    "Tracker",
                    "Discounted",
                  ]}
                />
              </Field>

              <Field
                label="Product Expiry Date"
                required
                error={errors.expiryDate}
              >
                <Input
                  dataKey="expiryDate"
                  type="date"
                  value={form.expiryDate}
                  onChange={(v) => set("expiryDate", v)}
                  disabled={isView}
                  isView={isView}
                />
              </Field>

              <Field label="Loan Purpose" required error={errors.purchaseType}>
                <Select
                  value={form.purchaseType}
                  onChange={(v) => set("purchaseType", v)}
                  disabled={isView}
                  isView={isView}
                  options={[
                    { value: "", label: "Select" },
                    "Purchase",
                    "Remortgage",
                    "Product Transfer",
                  ]}
                />
              </Field>

              <Field
                label="Mortgage Term (years)"
                required
                error={errors.mortgageTerm}
              >
                <Input
                  dataKey="mortgageTerm"
                  type="number"
                  value={form.mortgageTerm}
                  onChange={(v) => set("mortgageTerm", v)}
                  disabled={isView}
                  isView={isView}
                  min={0}
                  max={40}
                  step={1}
                  placeholder="0 - 40"
                />
              </Field>

              <Field
                label="Product Term (Years)"
                required
                error={errors.productTerm}
              >
                <Select
                  value={form.productTerm}
                  onChange={(v) => set("productTerm", v)}
                  disabled={isView}
                  isView={isView}
                  options={[
                    { value: "", label: "Select" },
                    "0.5",
                    "1",
                    "2",
                    "3",
                    "5",
                    "10",
                    "Lifetime",
                  ]}
                />
              </Field>

              <Field
                label="Borrower Type"
                required
                error={errors.borrowerType}
              >
                <Select
                  value={form.borrowerType}
                  onChange={(v) => set("borrowerType", v)}
                  disabled={isView}
                  isView={isView}
                  options={[
                    { value: "", label: "Select" },
                    "Individual/partnership",
                    "Ltd Co.",
                    "Pension/Trust",
                  ]}
                />
              </Field>

              <Field
                label="EPC rating — Is it a green product?"
                required
                error={errors.epcGreen}
              >
                <Select
                  value={form.epcGreen}
                  onChange={(v) => set("epcGreen", v)}
                  disabled={isView}
                  isView={isView}
                  options={[{ value: "", label: "Select" }, "Yes", "No", "0"]}
                />
              </Field>
            </Section>
          ) : null}

          {tab === "mortgage" ? (
            <Section title="Mortgage Details" icon={Percent}>
              <Field label="LTV (%)" required error={errors.ltv}>
                <Input
                  dataKey="ltv"
                  type="number"
                  value={form.ltv}
                  onChange={(v) => set("ltv", v)}
                  disabled={isView}
                  isView={isView}
                  min={0}
                  max={100}
                  step={0.01}
                  placeholder="e.g. 75"
                />
              </Field>
              <Field label="Min Loan (£)" required error={errors.minLoan}>
                <Input
                  dataKey="minLoan"
                  type="number"
                  value={form.minLoan}
                  onChange={(v) => set("minLoan", v)}
                  disabled={isView}
                  isView={isView}
                  min={0}
                  step={0.01}
                  placeholder="0.00"
                />
              </Field>
              <Field label="Max Loan (£)" required error={errors.maxLoan}>
                <Input
                  dataKey="maxLoan"
                  type="number"
                  value={form.maxLoan}
                  onChange={(v) => set("maxLoan", v)}
                  disabled={isView}
                  isView={isView}
                  min={0}
                  step={0.01}
                  placeholder="0.00"
                />
              </Field>
              <Field label="Product Rate (%)" required error={errors.rate}>
                <Input
                  dataKey="rate"
                  type="number"
                  value={form.rate}
                  onChange={(v) => set("rate", v)}
                  disabled={isView}
                  isView={isView}
                  min={0}
                  step={0.01}
                  placeholder="e.g. 4.49"
                />
              </Field>
              <Field label="APRC (%)" required error={errors.aprc}>
                <Input
                  dataKey="aprc"
                  type="number"
                  value={form.aprc}
                  onChange={(v) => set("aprc", v)}
                  disabled={isView}
                  isView={isView}
                  min={0}
                  step={0.01}
                  placeholder="e.g. 6.1"
                />
              </Field>
              <Field label="SVR Margin (%)" required error={errors.svr}>
                <Input
                  dataKey="svr"
                  type="number"
                  value={form.svr}
                  onChange={(v) => set("svr", v)}
                  disabled={isView}
                  isView={isView}
                  min={0}
                  step={0.01}
                  placeholder="e.g. 3.50"
                />
              </Field>
            </Section>
          ) : null}

          {tab === "coverage" ? (
            <Section title="Debt Service Coverage" icon={ShieldCheck}>
              <Field
                label="Basic Tax Payer DSC (%)"
                required
                error={errors.dscBasic}
              >
                <Input
                  dataKey="dscBasic"
                  type="number"
                  value={form.dscBasic}
                  onChange={(v) => set("dscBasic", v)}
                  disabled={isView}
                  isView={isView}
                  min={0}
                  step={0.01}
                  placeholder="≥ 100"
                />
              </Field>
              <Field
                label="High Tax Payer DSC (%)"
                required
                error={errors.dscHigh}
              >
                <Input
                  dataKey="dscHigh"
                  type="number"
                  value={form.dscHigh}
                  onChange={(v) => set("dscHigh", v)}
                  disabled={isView}
                  isView={isView}
                  min={0}
                  step={0.01}
                  placeholder="≥ 100"
                />
              </Field>
              <Field label="ICR Rate" required error={errors.icr}>
                <Input
                  dataKey="icr"
                  type="number"
                  value={form.icr}
                  onChange={(v) => set("icr", v)}
                  disabled={isView}
                  isView={isView}
                  min={0}
                  step={0.01}
                  placeholder="> 0"
                />
              </Field>
            </Section>
          ) : null}

          {tab === "fees" ? (
            <>
              <Section
                title="Fee Structure"
                icon={PoundSterling}
                hint="Product Fee supports £, %, and Min Fee"
              >
                <Field label="Product Fee" required error={errors.prodFee}>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <Input
                        dataKey="prodFee"
                        type="number"
                        value={form.prodFee}
                        onChange={(v) => set("prodFee", v)}
                        disabled={isView}
                        isView={isView}
                        min={0}
                        step={0.01}
                        placeholder="e.g. 995 / 1.50 / 1495"
                      />
                    </div>
                    <TogglePills
                      value={form.productFeeMode}
                      onChange={(v) => set("productFeeMode", v)}
                      disabled={isView}
                      options={[
                        { id: "pound", label: "£ Flat" },
                        { id: "percent", label: "% Loan" },
                        { id: "min", label: "Min £" },
                      ]}
                    />
                  </div>
                </Field>

                <Field
                  label="Application Fee (£)"
                  required
                  error={errors.appFee}
                >
                  <Input
                    dataKey="appFee"
                    type="number"
                    value={form.appFee}
                    onChange={(v) => set("appFee", v)}
                    disabled={isView}
                    isView={isView}
                    min={0}
                    step={0.01}
                    placeholder="0.00"
                  />
                </Field>
              </Section>

              <Section
                title="Early Repayment Charges"
                icon={AlertTriangle}
                hint="Years 1–5"
              >
                {["erc1", "erc2", "erc3", "erc4", "erc5"].map((k, i) => (
                  <Field key={k} label={`Year ${i + 1} (%)`} required error={errors[k]}>
                    <Input
                      dataKey={k}
                      type="number"
                      value={form[k]}
                      onChange={(v) => set(k, v)}
                      disabled={isView}
                      isView={isView}
                      min={0}
                      step={0.01}
                      placeholder={`e.g. ${5 - i}`}
                    />
                  </Field>
                ))}
              </Section>
            </>
          ) : null}

          {tab === "property" ? (
            <Section title="Property Requirements" icon={Building2}>
              <Field label="Home Owner" required error={errors.homeOwner}>
                <Select
                  value={form.homeOwner}
                  onChange={(v) => set("homeOwner", v)}
                  disabled={isView}
                  isView={isView}
                  options={[
                    { value: "", label: "Select" },
                    "Any",
                    "Yes",
                    "No",
                  ]}
                />
              </Field>

              <Field label="BTL Experience (years)" required error={errors.btlExp}>
                <Select
                  value={form.btlExp}
                  onChange={(v) => set("btlExp", v)}
                  disabled={isView}
                  isView={isView}
                  options={[
                    { value: "", label: "Select" },
                    "0",
                    "1-5",
                    "5+",
                  ]}
                />
              </Field>

              <Field
                label="Credit Worthiness"
                required
                error={errors.creditWorth}
              >
                <Select
                  value={form.creditWorth}
                  onChange={(v) => set("creditWorth", v)}
                  disabled={isView}
                  isView={isView}
                  options={[
                    { value: "", label: "Select" },
                    "Excellent 881+",
                    "Fair 721-880",
                    "Poor <550",
                  ]}
                />
              </Field>

              <Field label="Tenancy Type" required error={errors.tenancyType}>
                <Select
                  value={form.tenancyType}
                  onChange={(v) => set("tenancyType", v)}
                  disabled={isView}
                  isView={isView}
                  options={[
                    { value: "", label: "Select" },
                    "AST",
                    "Lease",
                    "License",
                    "None",
                  ]}
                />
              </Field>

              <Field label="F/H or L/H" required error={errors.tenure}>
                <Select
                  value={form.tenure}
                  onChange={(v) => set("tenure", v)}
                  disabled={isView}
                  isView={isView}
                  options={[
                    { value: "", label: "Select" },
                    "FREEHOLD",
                    "LEASEHOLD",
                  ]}
                />
              </Field>

              {form.tenure === "LEASEHOLD" ? (
                <Field
                  label="Leasehold Remaining Term (Years)"
                  required
                  error={errors.leaseYears}
                >
                  <Input
                    dataKey="leaseYears"
                    type="number"
                    value={form.leaseYears}
                    onChange={(v) => set("leaseYears", v)}
                    disabled={isView}
                    isView={isView}
                    min={0}
                    step={1}
                    placeholder="e.g. 85"
                  />
                </Field>
              ) : null}

              <Field label="Ex Local Authority" required error={errors.exLocal}>
                <Select
                  value={form.exLocal}
                  onChange={(v) => set("exLocal", v)}
                  disabled={isView}
                  isView={isView}
                  options={[{ value: "", label: "Select" }, "No", "Yes"]}
                />
              </Field>

              <Field
                label="Deposit Origination"
                required
                error={errors.depositOrig}
              >
                <Select
                  value={form.depositOrig}
                  onChange={(v) => set("depositOrig", v)}
                  disabled={isView}
                  isView={isView}
                  options={[
                    { value: "", label: "Select" },
                    "Savings",
                    "Gifted",
                    "Equity",
                  ]}
                />
              </Field>

              <Field label="Day 1 Remortgage" required error={errors.day1Remo}>
                <Select
                  value={form.day1Remo}
                  onChange={(v) => set("day1Remo", v)}
                  disabled={isView}
                  isView={isView}
                  options={[
                    { value: "", label: "Select" },
                    "Pound for Pound",
                    "Market Value",
                    "Refurb",
                  ]}
                />
              </Field>
              <Field label="Cashback (£)" required={false} error={errors.cashback}>
                <Input
                  dataKey="cashback"
                  type="number"
                  value={form.cashback}
                  onChange={(v) => set("cashback", v)}
                  disabled={isView}
                  isView={isView}
                  min={0}
                  step={0.01}
                  placeholder="0"
                />
              </Field>
            </Section>
          ) : null}

          {tab === "docs" ? (
            <Section
              title="Valuation & Legal Documents"
              icon={FileUp}
              hint={`Spreadsheets only (.csv/.xls/.xlsx/.ods) — max ${MAX_MB}MB, or numeric values`}
            >
              <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Valuation Fee - can be file or numeric value */}
                <div>
                  <div className="text-sm font-semibold text-slate-900 mb-2">
                    Valuation Fee Schedule
                  </div>
                  <div className="text-xs text-slate-500 mb-3">
                    Required unless Free Valuation is selected. Can be a file or numeric value.
                  </div>
                  {form.valuationDoc ? (
                    <FileCard
                      title="Valuation Fee Schedule"
                      subtitle="File uploaded"
                      file={form.valuationDoc}
                      disabled={isView || !!form.features?.["Free Valuation"]}
                      onPick={() => fileValRef.current?.click()}
                      onClear={() => set("valuationDoc", null)}
                    />
                  ) : form.valuationFee ? (
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="text-sm font-semibold text-slate-900">
                        Numeric Value: £{Number(form.valuationFee).toLocaleString()}
                      </div>
                      {!isView && (
                        <button
                          type="button"
                          onClick={() => {
                            set("valuationFee", null);
                            fileValRef.current?.click();
                          }}
                          className="mt-2 text-xs text-sky-600 hover:text-sky-700"
                        >
                          Replace with file
                        </button>
                      )}
                    </div>
                  ) : isView ? (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-sm text-slate-500">No valuation fee specified</div>
                    </div>
                  ) : (
                    <>
                      <FileCard
                        title="Valuation Fee Schedule"
                        subtitle="Upload file or enter numeric value"
                        file={null}
                        disabled={isView || !!form.features?.["Free Valuation"]}
                        onPick={() => fileValRef.current?.click()}
                        onClear={() => {}}
                      />
                      <div className="mt-2">
                        <Field label="Or enter numeric value (£)" required={false}>
                          <Input
                            dataKey="valuationFee"
                            type="number"
                            value={form.valuationFee || ""}
                            onChange={(v) => set("valuationFee", v)}
                            disabled={isView || !!form.features?.["Free Valuation"]}
                            min={0}
                            step={0.01}
                            placeholder="e.g. 500"
                          />
                        </Field>
                      </div>
                    </>
                  )}
                  <input
                    ref={fileValRef}
                    type="file"
                    accept=".csv,.xls,.xlsx,.ods"
                    className="hidden"
                    onChange={(e) => {
                      set("valuationDoc", e.target.files?.[0] || null);
                      set("valuationFee", null); // Clear numeric value when file is selected
                    }}
                  />
                  {errors.valuationDoc ? (
                    <div className="mt-2 text-xs text-rose-600">
                      {errors.valuationDoc}
                    </div>
                  ) : null}
                </div>

                {/* Legal Fees - can be file or numeric value */}
                <div>
                  <div className="text-sm font-semibold text-slate-900 mb-2">
                    Legal Fees Schedule
                  </div>
                  <div className="text-xs text-slate-500 mb-3">
                    Required for all products. Can be a file or numeric value.
                  </div>
                  {form.legalFeesDoc ? (
                    <FileCard
                      title="Legal Fees Schedule"
                      subtitle="File uploaded"
                      file={form.legalFeesDoc}
                      disabled={isView}
                      onPick={() => fileLegalRef.current?.click()}
                      onClear={() => set("legalFeesDoc", null)}
                    />
                  ) : form.legalFee ? (
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="text-sm font-semibold text-slate-900">
                        Numeric Value: £{Number(form.legalFee).toLocaleString()}
                      </div>
                      {!isView && (
                        <button
                          type="button"
                          onClick={() => {
                            set("legalFee", null);
                            fileLegalRef.current?.click();
                          }}
                          className="mt-2 text-xs text-sky-600 hover:text-sky-700"
                        >
                          Replace with file
                        </button>
                      )}
                    </div>
                  ) : isView ? (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-sm text-slate-500">No legal fee specified</div>
                    </div>
                  ) : (
                    <>
                      <FileCard
                        title="Legal Fees Schedule"
                        subtitle="Upload file or enter numeric value"
                        file={null}
                        disabled={isView}
                        onPick={() => fileLegalRef.current?.click()}
                        onClear={() => {}}
                      />
                      <div className="mt-2">
                        <Field label="Or enter numeric value (£)" required={false}>
                          <Input
                            dataKey="legalFee"
                            type="number"
                            value={form.legalFee || ""}
                            onChange={(v) => set("legalFee", v)}
                            disabled={isView}
                            min={0}
                            step={0.01}
                            placeholder="e.g. 300"
                          />
                        </Field>
                      </div>
                    </>
                  )}
                  <input
                    ref={fileLegalRef}
                    type="file"
                    accept=".csv,.xls,.xlsx,.ods"
                    className="hidden"
                    onChange={(e) => {
                      set("legalFeesDoc", e.target.files?.[0] || null);
                      set("legalFee", null); // Clear numeric value when file is selected
                    }}
                  />
                  {errors.legalFeesDoc ? (
                    <div className="mt-2 text-xs text-rose-600">
                      {errors.legalFeesDoc}
                    </div>
                  ) : null}
                </div>
              </div>
            </Section>
          ) : null}

          {tab === "schedule" ? (
            <Section
              title="Scheduling"
              icon={CalendarDays}
              hint="Optional go-live scheduling"
            >
              <div className="md:col-span-3 flex flex-col md:flex-row md:items-center gap-3">
                <label className="inline-flex items-center gap-2 text-slate-800 font-medium">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={!!form.scheduleEnabled}
                    disabled={isView}
                    onChange={(e) => set("scheduleEnabled", e.target.checked)}
                  />
                  Schedule this product to go live later
                </label>

                <div className="flex-1">
                  <Input
                    dataKey="scheduleAt"
                    type="datetime-local"
                    value={form.scheduleAt}
                    onChange={(v) => set("scheduleAt", v)}
                    disabled={isView || !form.scheduleEnabled}
                  />
                  {errors.scheduleAt ? (
                    <div className="text-xs text-rose-600 mt-1">
                      {errors.scheduleAt}
                    </div>
                  ) : null}
                </div>
              </div>
            </Section>
          ) : null}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            {isView ? "Read-only view" : "Make changes then Save"}
          </div>
          <div className="flex items-center gap-2">
            {!isView ? (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  className="px-4 py-2 text-sm font-medium text-white bg-sky-500 rounded-lg hover:bg-sky-600 transition"
                >
                  Save Changes
                </button>
              </>
            ) : (
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* =============================================================================
   CSV Import Modal — follows SAME pattern as Add Product
============================================================================= */
function CsvImportModal({ onClose, onImport, lenders = [], products = [], defaultProductLine = "BTL" }) {
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const fileRef = useRef(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [productLine, setProductLine] = useState(
    defaultProductLine === "IF" ? "IF" : "BTL"
  );
  const isIF = productLine === "IF";

  const [meta, setMeta] = useState(() => ({
    scheduleEnabled: false,
    scheduleAt: "",
    lender: "",
    lenderId: "",
    lenderEmail: "",
  }));

  const validateBasic = () => {
    const e = {};
    if (!file) e.file = "CSV/Spreadsheet file is required";
    else if (!allowedSheetExt.test(file.name)) e.file = "Upload .csv/.xls/.xlsx/.ods";
    else if (file.size > MAX_MB * 1024 * 1024) e.file = `File > ${MAX_MB}MB`;

    if (!isIF && meta.scheduleEnabled && !meta.scheduleAt) e.scheduleAt = "Choose a schedule date/time";
    
    if (!isIF && !meta.lenderEmail && !meta.lender) {
      e.lender = "Please enter lender email address for these products";
    }
    
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateCSV = async () => {
    if (isIF) {
      toast("Invoice Finance uploads skip client-side CSV validation — use the Dynesourcer IF template.", {
        icon: "ℹ️",
      });
      setValidationResult({
        valid: true,
        stats: { totalRows: "—" },
        errors: [],
        warnings: [],
      });
      return;
    }

    if (!validateBasic()) {
      toast.error("Please fix basic errors first");
      return;
    }

    setIsValidating(true);
    setValidationResult(null);
    setErrors({});

    try {
      const result = await validateCSVFile(file);
      setValidationResult(result);

      if (result.valid) {
        toast.success(`Validation passed! ${result.stats.totalRows} row(s) ready to upload.`);
      } else {
        toast.error(`Validation failed: ${result.errors.length} error(s) found`);
      }
    } catch (error) {
      console.error("Validation error:", error);
      toast.error(`Validation error: ${error.message}`);
      setValidationResult({
        valid: false,
        errors: [error.message],
        warnings: [],
      });
    } finally {
      setIsValidating(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      if (isIF) {
        toast("Use the Invoice Finance XLSX template provided by Dynesourcer.", { icon: "ℹ️" });
        return;
      }
      const response = await fetch("/Dynesourcer_CSV_Template.csv");
      if (!response.ok) {
        // Fallback to generating template
        const headers = TEMPLATE_FIELDS;
        const row = headers.map(() => "").join(",");
        const csv = `${headers.join(",")}\n${row}\n`;
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `product_import_template_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("Template downloaded successfully");
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Dynesourcer_CSV_Template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded successfully");
    } catch (error) {
      console.error("Failed to download template:", error);
      toast.error("Failed to download template file");
    }
  };

  const processUpload = async () => {
    if (!validateBasic()) return;

    if (!isIF) {
      if (!validationResult) {
        toast.error("Please validate the file first before uploading");
        return;
      }

      if (!validationResult.valid) {
        toast.error("Please fix validation errors before uploading");
        return;
      }
    }

    setIsUploading(true);
    setErrors({});

    try {
      if (isIF) {
        const imported = await uploadAndImportInvoiceFinanceProducts(file);
        const created = imported?.created ?? 0;
        const updated = imported?.updated ?? 0;
        const failed = imported?.failed ?? 0;
        toast.success(
          `IF import finished — created ${created}, updated ${updated}${failed ? `, failed ${failed}` : ""}`
        );
        if (onImport) {
          onImport({ file, meta: { ...meta, productLine: "IF" }, upload: null, imported });
        }
        setTimeout(() => onClose(), 1500);
        return;
      }

      // Get lender email from lender selection
      // If lender is selected by name, we need to find the email
      // For now, use lender name as email if email is not provided
      const lenderEmail = meta.lenderEmail || meta.lender || meta.lenderId;
      
      if (!lenderEmail) {
        toast.error("Please select a lender before uploading.");
        return;
      }
      
      const { upload, imported } = await uploadAndImportMortgageProducts({
        file,
        product_type: "",
        scheduleEnabled: meta.scheduleEnabled,
        scheduleAt: meta.scheduleAt,
        lender_id: meta.lenderId,
        lender_name: meta.lender,
        lender_email: lenderEmail,
      });

      console.log("📁 CSV upload info:", upload);
      console.log("📊 Mortgage import info:", imported);

      toast.success(
        `Successfully imported ${validationResult.stats.totalRows} product(s) for ${meta.lender || "selected lender"}`
      );

      // Call the original onImport callback
      if (onImport) {
        onImport({ file, meta: { ...meta, productLine: "BTL" }, upload, imported });
      }

      // Close modal after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error("❌ CSV Import failed:", error);
      const serverMsg =
        error?.data?.message ||
        error?.data?._server_messages ||
        error?.message ||
        "Upload/import failed. Please try again.";

      setErrors({
        upload: serverMsg,
      });
      toast.error(serverMsg);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-sky-50 border border-sky-200 flex items-center justify-center">
              <Upload className="h-5 w-5 text-sky-700" />
            </div>
            <div>
              <div className="text-base font-semibold text-slate-900">Import Products (CSV)</div>
              <div className="text-xs text-slate-500">
                {isIF
                  ? "Upload Invoice Finance products from the Dynesourcer IF template"
                  : "Upload BTL mortgage products with a single CSV file"}
              </div>
            </div>
          </div>

          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition" title="Close">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="border-2 border-dashed border-sky-200 rounded-2xl m-6">
            <div className="px-6 pt-12 pb-6 flex flex-col items-stretch space-y-10">
              <div className="mx-auto max-w-2xl w-full">
                <div className="text-sm font-semibold text-slate-800 mb-2">Product Line</div>
                <div className="flex gap-2">
                  {PRODUCT_LINE_OPTIONS.map((line) => (
                    <button
                      key={line}
                      type="button"
                      disabled={isUploading}
                      onClick={() => {
                        setProductLine(line);
                        setValidationResult(null);
                        setErrors({});
                      }}
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                        productLine === line
                          ? "bg-sky-50 text-sky-700 border-sky-300"
                          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      {line === "IF" ? "Invoice Finance (IF)" : "Buy-to-Let (BTL)"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Lender Selection (BTL only) */}
              {!isIF ? (
              <div className="mx-auto max-w-2xl w-full">
                <div className="text-sm font-semibold text-slate-800 mb-2">Select Lender (Email Required)</div>
                <input
                  type="email"
                  value={meta.lenderEmail || meta.lender || ""}
                  onChange={(e) => {
                    const email = e.target.value;
                    setMeta((s) => ({
                      ...s,
                      lenderEmail: email,
                      lender: email, // Use email as lender name too
                    }));
                    setErrors((err) => ({ ...err, lender: undefined }));
                  }}
                  disabled={isUploading}
                  placeholder="Enter lender email (e.g. lender@example.com)"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400 disabled:bg-slate-100"
                />
                {errors.lender && (
                  <div className="mt-1 text-xs text-red-600">{errors.lender}</div>
                )}
                <div className="text-xs text-slate-500 mt-1">
                  All products in this CSV will be uploaded to the selected lender's account. Please enter the lender's email address.
                </div>
              </div>
              ) : null}

              {/* Validation Status */}
              {validationResult && (
                <div className={`mx-auto max-w-2xl w-full rounded-lg p-4 border ${
                  validationResult.valid
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-red-50 border-red-200"
                }`}>
                  <div className="flex items-start gap-3">
                    {validationResult.valid ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${
                        validationResult.valid ? "text-emerald-800" : "text-red-800"
                      }`}>
                        {validationResult.valid
                          ? `Validation passed! ${validationResult.stats.totalRows} row(s) ready to upload.`
                          : `Validation failed: ${validationResult.errors.length} error(s) found`}
                      </p>
                      {validationResult.warnings && validationResult.warnings.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-amber-700 font-medium mb-1">
                            Warnings ({validationResult.warnings.length}):
                          </p>
                          <ul className="text-xs text-amber-600 list-disc list-inside space-y-0.5">
                            {validationResult.warnings.slice(0, 5).map((w, idx) => (
                              <li key={idx}>{w}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {!validationResult.valid && validationResult.errors && validationResult.errors.length > 0 && (
                        <div className="mt-2 max-h-60 overflow-y-auto">
                          <p className="text-xs text-red-700 font-medium mb-1">
                            Errors ({validationResult.errors.length}):
                          </p>
                          <ul className="text-xs text-red-600 list-disc list-inside space-y-0.5">
                            {validationResult.errors.slice(0, 20).map((e, idx) => (
                              <li key={idx}>{e}</li>
                            ))}
                            {validationResult.errors.length > 20 && (
                              <li className="text-red-500 font-medium">
                                ... and {validationResult.errors.length - 20} more error(s)
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Upload widget */}
              <div className="text-center">
                <div className="mx-auto w-16 h-16 rounded-2xl bg-sky-50 border border-sky-100 grid place-items-center">
                  <FileSpreadsheet
                    className="h-12 w-12 text-sky-700"
                    aria-hidden="true"
                  />
                </div>

                <h2 className="mt-4 text-lg font-semibold text-slate-900">
                  Upload Product CSV File
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Upload a CSV containing multiple products following our
                  predefined format
                </p>

                <div className="mt-5">
                  <label className="inline-flex items-center">
                    <span className="relative">
                      <button
                        type="button"
                        className="px-4 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-700 disabled:opacity-50"
                        disabled={isUploading}
                      >
                        {isUploading ? "Uploading..." : "Select File"}
                      </button>
                      <input
                        ref={fileRef}
                        type="file"
                        accept=".csv,.xls,.xlsx"
                        className="absolute inset-0 h-full w-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                        onChange={(e) => {
                          setFile(e.target.files?.[0] || null);
                          setValidationResult(null);
                          setErrors((prev) => ({ ...prev, file: undefined }));
                        }}
                        disabled={isUploading}
                      />
                    </span>
                  </label>
                </div>

                <div className="mt-2 text-xs text-slate-500">
                  {file ? file.name : "No file selected"}
                </div>
                {errors.file && (
                  <div className="mt-1 text-xs text-red-600">
                    {errors.file}
                  </div>
                )}
              </div>

              {/* Required CSV Format */}
              <div className="max-w-md mx-auto md:pl-1">
                <div className="text-sm font-semibold text-slate-800">
                  Required CSV Format:
                </div>
                <ul className="mt-2 text-sm text-slate-600 list-disc list-inside space-y-1">
                  <li>First row must be headers matching field names</li>
                  <li>Date formats: YYYY-MM-DD</li>
                  <li>Column order doesn't matter</li>
                  <li>Maximum file size: 10MB</li>
                </ul>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="mt-3 text-sky-700 hover:text-sky-800 font-medium disabled:opacity-50"
                  disabled={isUploading}
                >
                  Download CSV Template
                </button>
              </div>

              {/* Action Buttons - Validate and Schedule side by side */}
              <div className="pt-2 pb-6 flex flex-col items-center gap-4">
                <div className="flex items-center gap-4">
                  {isIF ? (
                    <button
                      type="button"
                      onClick={processUpload}
                      className="px-5 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-700 disabled:opacity-60 flex items-center gap-2"
                      disabled={!file || isUploading}
                    >
                      {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
                      {isUploading ? "Uploading..." : "Process IF Upload"}
                    </button>
                  ) : !validationResult?.valid ? (
                    <button
                      type="button"
                      onClick={validateCSV}
                      className="px-5 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-700 disabled:opacity-60 flex items-center gap-2"
                      disabled={!file || isUploading || isValidating || (!meta.lender && !meta.lenderId)}
                    >
                      {isValidating && (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      )}
                      {isValidating ? "Validating..." : "Validate"}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={processUpload}
                        className="px-5 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-700 disabled:opacity-60 flex items-center gap-2"
                        disabled={!file || isUploading || !meta.lenderEmail}
                      >
                        {isUploading && (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        )}
                        {isUploading ? "Uploading..." : "Process Upload"}
                      </button>
                      
                      {/* Schedule Option - Only shown after successful validation */}
                      <div className="flex items-center gap-3 rounded-lg border border-sky-200 bg-sky-50/50 px-4 py-2">
                        <label className="inline-flex items-center gap-2 text-sky-900 font-semibold text-sm">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={meta.scheduleEnabled}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setMeta((s) => ({ ...s, scheduleEnabled: checked }));
                              setErrors((prev) => ({
                                ...prev,
                                scheduleAt: checked ? prev.scheduleAt : undefined,
                              }));
                            }}
                            disabled={isUploading}
                          />
                          Schedule
                        </label>
                        <input
                          type="datetime-local"
                          className={`border rounded p-2 bg-white text-sm ${errors.scheduleAt ? "border-red-500" : ""}`}
                          disabled={!meta.scheduleEnabled || isUploading}
                          value={meta.scheduleAt}
                          onChange={(e) => setMeta((s) => ({ ...s, scheduleAt: e.target.value }))}
                        />
                      </div>
                      {meta.scheduleEnabled && (
                        <button
                          type="button"
                          onClick={processUpload}
                          className="px-5 py-2 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-2"
                          disabled={!file || isUploading || (!meta.lender && !meta.lenderId) || !meta.scheduleAt}
                        >
                          {isUploading && (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          )}
                          {isUploading ? "Scheduling..." : "Schedule Upload"}
                        </button>
                      )}
                    </>
                  )}
                </div>
                {errors.scheduleAt && (
                  <p className="text-red-600 text-sm">
                    {errors.scheduleAt}
                  </p>
                )}
              </div>

              {errors.upload && (
                <div className="mx-auto max-w-2xl w-full rounded-lg p-4 border bg-red-50 border-red-200">
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm font-medium text-red-800">{errors.upload}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =============================================================================
   Small helpers
============================================================================= */
function MiniInput({ label, value, onChange, placeholder }) {
  return (
    <label className="block">
      <div className="text-[11px] font-medium text-slate-600 mb-1">{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white"
      />
    </label>
  );
}

/* =============================================================================
   Main Page
============================================================================= */
export default function ProductManagementPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // View/Edit/Create modals
  const [modal, setModal] = useState({ open: false, mode: "view", product: null });

  // CSV modal
  const [csvOpen, setCsvOpen] = useState(false);

  // Confirm modals
  const [confirm, setConfirm] = useState(null);

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const selectionAnchorIdRef = useRef(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const line = String(params.get("line") || "").toUpperCase();
      return line === "IF" ? "IF" : "BTL";
    } catch {
      return "BTL";
    }
  });
  const [lenderFilter, setLenderFilter] = useState("");
  const [rateTypeFilter, setRateTypeFilter] = useState("");
  const [showScheduledOnly, setShowScheduledOnly] = useState(false);

  // Advanced filters (collapsed by default)
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [minRate, setMinRate] = useState("");
  const [maxRate, setMaxRate] = useState("");
  const [minLtv, setMinLtv] = useState("");
  const [maxLtv, setMaxLtv] = useState("");
  const [minLoan, setMinLoan] = useState("");
  const [maxLoan, setMaxLoan] = useState("");

  // Sorting
  const [sortBy, setSortBy] = useState("updatedAt");
  const [sortOrder, setSortOrder] = useState("desc");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200, 500];
  const ALL_PAGE_SIZE = 1000000; // "All" option

  // State for filter mode (simple vs advanced)
  const [useAdvancedFilter, setUseAdvancedFilter] = useState(false);
  const [kpiTick, setKpiTick] = useState(0);
  
  /* ======= Fetch products from API ======= */
  const fetchProducts = async (filterParams = {}) => {
    setLoading(true);
    try {
      const line = String(typeFilter || "BTL").toUpperCase() === "IF" ? "IF" : "BTL";
      let normalized = [];

      if (line === "IF") {
        // Load all IF rows, then filter client-side after status normalization
        // (Deactivated may be stored as status or disabled=1).
        let [ifData, lenderNameByEmail] = await Promise.all([
          getAllInvoiceFinanceProducts(),
          fetchLenderNameByEmail().catch((err) => {
            console.warn("Failed to load lender names for IF products:", err);
            return new Map();
          }),
        ]);
        if (!Array.isArray(ifData)) ifData = [];
        normalized = ifData.map((p) => mapIfToAdminProduct(p, lenderNameByEmail));
      } else {
        const params = buildSimpleFilterParams(filterParams);
        params.product_category = "BTL";
        if (sortBy) {
          let orderField = sortBy;
          if (sortBy === "rate") orderField = "product_rate_percent";
          else if (sortBy === "updatedAt") orderField = "modified";
          else if (sortBy === "lender") orderField = "lender_name";
          else if (sortBy === "type") orderField = "product_category";
          else if (sortBy === "status") orderField = "status";
          params.order_by = `${orderField} ${sortOrder}`;
        }
        let productsData = await getAllMortgageProducts(params);
        if (!Array.isArray(productsData)) productsData = [];
        normalized = productsData
          .filter((p) => !isInvoiceFinanceProduct(p))
          .map(mapBtlToAdminProduct);
      }

      setProducts(normalized);
      setKpiTick((t) => t + 1);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products. Please try again.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Build simple filter params
  const buildSimpleFilterParams = (extraParams = {}) => {
    const params = { ...extraParams };
    const q = searchTerm.trim();
    if (statusFilter) {
      params.status = statusFilter;
    }
    if (typeFilter && typeFilter !== "IF" && typeFilter !== "BTL") {
      params.product_category = typeFilter;
    } else if (typeFilter === "BTL") {
      params.product_category = "BTL";
    }
    if (lenderFilter) {
      params.lender_name__like = lenderFilter;
    }
    if (rateTypeFilter) {
      params.product_type = rateTypeFilter;
    }
    if (minRate) {
      params.product_rate_percent__gte = Number(minRate);
    }
    if (maxRate) {
      params.product_rate_percent__lte = Number(maxRate);
    }
    if (minLtv) {
      params.ity_percent__gte = Number(minLtv);
    }
    if (maxLtv) {
      params.ity_percent__lte = Number(maxLtv);
    }
    if (minLoan) {
      params.min_loan__gte = Number(minLoan);
    }
    if (maxLoan) {
      params.max_loan__lte = Number(maxLoan);
    }
    if (showScheduledOnly) {
      params.product_schedule = 1;
    }
    
    return params;
  };
  
  // Build advanced filter params
  const buildAdvancedFilterParams = (extraParams = {}) => {
    const params = { ...extraParams };
    const q = searchTerm.trim();
    const looksLikeProductId = /-[0-9]+$/i.test(q) || /^[A-Za-z]+-[0-9]+$/.test(q);
    if (q && !looksLikeProductId) {
      params.product_name__like = q;
    }
    if (statusFilter) {
      params.status = statusFilter;
    }
    if (typeFilter && typeFilter !== "IF" && typeFilter !== "BTL") {
      params.product_category = typeFilter;
    } else if (typeFilter === "BTL") {
      params.product_category = "BTL";
    }
    if (lenderFilter) {
      params.lender_name__like = lenderFilter;
    }
    if (rateTypeFilter) {
      params.product_type = rateTypeFilter;
    }
    if (minRate) {
      params.product_rate_percent__gte = Number(minRate);
    }
    if (maxRate) {
      params.product_rate_percent__lte = Number(maxRate);
    }
    if (minLtv) {
      params.ity_percent__gte = Number(minLtv);
    }
    if (maxLtv) {
      params.ity_percent__lte = Number(maxLtv);
    }
    if (minLoan) {
      params.min_loan = Number(minLoan);
    }
    if (maxLoan) {
      params.max_loan = Number(maxLoan);
    }
    if (showScheduledOnly) {
      params.product_schedule = 1;
    }
    
    // Advanced filter supports order_by, limit_start, limit_page_length
    if (sortBy) {
      let orderField = sortBy;
      if (sortBy === "rate") orderField = "product_rate_percent";
      else if (sortBy === "updatedAt") orderField = "modified";
      else if (sortBy === "lender") orderField = "lender_name";
      else if (sortBy === "type") orderField = "product_category";
      else if (sortBy === "status") orderField = "status";
      
      params.order_by = `${orderField} ${sortOrder}`;
    }
    
    params.limit_start = (currentPage - 1) * itemsPerPage;
    params.limit_page_length = itemsPerPage;
    
    return params;
  };

  const upsertProduct = async (p) => {
    try {
      // Upload files first if new files are being uploaded
      let valuationFileUrl = null;
      let legalFeesFileUrl = null;
      
      // Upload valuation file if it's a new File object
      if (p.valuationDoc && p.valuationDoc instanceof File) {
        console.log("⬆️ Uploading valuation file:", p.valuationDoc.name);
        try {
          valuationFileUrl = await __productsInternals.uploadFileGetUrl(
            p.valuationDoc,
            { doctype: "Mortgage Product", name: p._raw?.name },
            null // no progress callback for now
          );
          console.log("✅ Valuation file uploaded:", valuationFileUrl);
        } catch (error) {
          console.error("❌ Error uploading valuation file:", error);
          toast.error(`Failed to upload valuation file: ${error.message}`);
          throw error;
        }
      }
      
      // Upload legal fees file if it's a new File object
      if (p.legalFeesDoc && p.legalFeesDoc instanceof File) {
        console.log("⬆️ Uploading legal fees file:", p.legalFeesDoc.name);
        try {
          legalFeesFileUrl = await __productsInternals.uploadFileGetUrl(
            p.legalFeesDoc,
            { doctype: "Mortgage Product", name: p._raw?.name },
            null // no progress callback for now
          );
          console.log("✅ Legal fees file uploaded:", legalFeesFileUrl);
        } catch (error) {
          console.error("❌ Error uploading legal fees file:", error);
          toast.error(`Failed to upload legal fees file: ${error.message}`);
          throw error;
        }
      }
      
      // Map all form data to API format
      const apiPayload = {
        product_name: p.productName || p.name,
        product_type: p.productType,
        product_category: p.type,
        // Backend only accepts "Active" or "Deactivated", not "Pending" or "Scheduled"
        // Map invalid statuses to valid ones
        status: (() => {
          const status = p.status || p._raw?.status || "Active";
          if (status === "Pending" || status === "Scheduled") {
            return "Active"; // Map Pending/Scheduled to Active
          }
          if (status === "Deactivated") {
            return "Deactivated";
          }
          return "Active"; // Default to Active
        })(),
        product_rate_percent: p.rate ? Number(p.rate) : null,
        mortgage_term_years: p.mortgageTerm ? Number(p.mortgageTerm) : null,
        product_termsyears: p.productTerm ? String(p.productTerm) : null,
        product_fee_amount: p.prodFee ? Number(p.prodFee) : null,
        product_fee_type: p.productFeeMode === "percent" ? "Percentage" : p.productFeeMode === "min" ? "Mini Fee" : "Pound",
        application_fee: p.appFee ? Number(p.appFee) : null,
        min_loan: p.minLoan ? Number(p.minLoan) : null,
        max_loan: p.maxLoan ? Number(p.maxLoan) : null,
        ity_percent: p.ltv ? Number(p.ltv) : null,
        aprc_percent: p.aprc ? Number(p.aprc) : null,
        svr_margin_percent: p.svr ? Number(p.svr) : null,
        basic_taxpayer_dsc_percent: p.dscBasic ? Number(p.dscBasic) : null,
        high_taxpayer_dsc: p.dscHigh ? Number(p.dscHigh) : null,
        icr_rate: p.icr ? Number(p.icr) : null,
        loan_purpose: p.purchaseType || null,
        borrower_type: p.borrowerType || null,
        epc_rating_green: p.epcGreen || null,
        product_expiray_date: p.expiryDate || null,
        home_owner: p.homeOwner || null,
        btl_experince_year: p.btlExp ? String(p.btlExp) : null,
        credit_worthiness: p.creditWorth || null,
        tenancy_type: p.tenancyType || null,
        fh_or_lh: p.tenure || null,
        // leasehold_remaining_termyears is mandatory - send 0 if not provided
        leasehold_remaining_termyears: p.leaseYears ? Number(p.leaseYears) : (p._raw?.leasehold_remaining_termyears ?? 0),
        ex_local_authority: p.exLocal || null,
        deposit_originantion: p.depositOrig || null,
        day_1_remortgage: p.day1Remo || null,
        cashback: p.cashback ? Number(p.cashback) : null,
        // ERC fields
        year_1: p.erc1 ? Number(p.erc1) : null,
        year_2: p.erc2 ? Number(p.erc2) : null,
        year_3: p.erc3 ? Number(p.erc3) : null,
        year_4: p.erc4 ? Number(p.erc4) : null,
        year_5: p.erc5 ? Number(p.erc5) : null,
        // Features (boolean fields - 0 or 1)
        portable: p.features?.Portable ? 1 : 0,
        overpayents: p.features?.Overpayments ? 1 : 0,
        underpayments: p.features?.Underpayments ? 1 : 0,
        payment_holiday: p.features?.["Payment Holiday"] ? 1 : 0,
        free_legals: p.features?.["Free Legals"] ? 1 : 0,
        free_valuation: p.features?.["Free Valuation"] ? 1 : 0,
        // Schedule
        product_schedule: p.scheduleEnabled ? 1 : 0,
        date_time: p.scheduleAt || null,
        // Valuation and legal fees - handle both file URLs and numeric values
        // Priority: newly uploaded file URL > existing URL string > numeric value > preserve from _raw
        valuation_fee_spreadsheet: (() => {
          // If file was just uploaded, use the new URL
          if (valuationFileUrl) {
            return valuationFileUrl;
          }
          // If it's a string (existing URL from backend), keep it
          if (p.valuationDoc && typeof p.valuationDoc === 'string') {
            return p.valuationDoc;
          }
          // If numeric value is set, convert to string
          if (p.valuationFee !== null && p.valuationFee !== undefined && p.valuationFee !== "") {
            return String(p.valuationFee);
          }
          // Preserve existing value from backend if nothing changed
          if (p._raw?.valuation_fee_spreadsheet) {
            return String(p._raw.valuation_fee_spreadsheet);
          }
          return null;
        })(),
        leagal_fees_spreadsheet: (() => {
          // If file was just uploaded, use the new URL
          if (legalFeesFileUrl) {
            return legalFeesFileUrl;
          }
          // If it's a string (existing URL from backend), keep it
          if (p.legalFeesDoc && typeof p.legalFeesDoc === 'string') {
            return p.legalFeesDoc;
          }
          // If numeric value is set, convert to string
          if (p.legalFee !== null && p.legalFee !== undefined && p.legalFee !== "") {
            return String(p.legalFee);
          }
          // Preserve existing value from backend if nothing changed
          if (p._raw?.leagal_fees_spreadsheet) {
            return String(p._raw.leagal_fees_spreadsheet);
          }
          return null;
        })(),
      };
      
      // For updates, send all fields that have values
      // Only remove undefined values, but keep null and 0 for mandatory fields
      // Special handling: leasehold_remaining_termyears is mandatory, so always include it
      Object.keys(apiPayload).forEach(key => {
        if (apiPayload[key] === undefined) {
          delete apiPayload[key];
        }
      });
      
      // Ensure mandatory fields are always included
      if (apiPayload.leasehold_remaining_termyears === undefined || apiPayload.leasehold_remaining_termyears === null) {
        apiPayload.leasehold_remaining_termyears = 0;
      }
      
      console.log("📦 Final API payload (after cleanup):", apiPayload);
      console.log("📦 Payload keys count:", Object.keys(apiPayload).length);
      
      // Get the document name for the update
      // The document name is the unique identifier used in the API endpoint:
      // PUT /api/resource/Mortgage Product/{docname}
      // Example: PUT /api/resource/Mortgage Product/BTL-003716
      // The docname comes from the backend's 'name' field in the raw data
      const docname = p._raw?.name || p._id || p.id;
      
      console.log("upsertProduct - Product data:", {
        id: p.id,
        _id: p._id,
        _rawName: p._raw?.name,
        docname: docname,
        fullProduct: p
      });
      
      if (docname && docname !== "UNKNOWN") {
        // Update existing product using PUT /api/resource/Mortgage Product/{docname}
        console.log("🔄 Updating product via API:");
        console.log("  - Endpoint: PUT /api/resource/Mortgage Product/" + docname);
        console.log("  - Payload keys:", Object.keys(apiPayload));
        console.log("  - Full payload:", JSON.stringify(apiPayload, null, 2));
        
        const response = await updateMortgageProduct(docname, apiPayload);
        console.log("✅ Update response:", response);
        
        // Refresh products list
        await fetchProducts();
        
        // Return success info for confirmation modal
        return {
          success: true,
          productName: p.productName || p.name || docname,
        };
      } else {
        // Create new product - would need create API endpoint
        console.error("❌ No valid document name found for product update:", {
          docname,
          id: p.id,
          _id: p._id,
          _raw: p._raw
        });
        toast.error("Unable to identify product for update. Please refresh and try again.");
        throw new Error("No valid document name found");
      }
    } catch (error) {
      console.error("❌ Error saving product:", error);
      console.error("❌ Error response:", error?.response);
      console.error("❌ Error data:", error?.response?.data);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.exception || error?.message || "Failed to save product. Please try again.";
      toast.error(errorMessage);
      throw error; // Re-throw so submit function knows it failed
    }
  };

  const bulkUpdateStatus = async (ids, status) => {
    try {
      const byId = new Map(products.map((p) => [p.id, p]));
      await Promise.all(
        ids.map((id) => {
          const row = byId.get(id);
          if (row?._productLine === "IF" || isInvoiceFinanceProduct(row || id)) {
            return updateInvoiceFinanceProduct(id, { status });
          }
          return updateMortgageProduct(id, { status });
        })
      );
      
      // Refresh products list
      await fetchProducts();
      toast.success(`Successfully ${status === "Active" ? "activated" : "deactivated"} ${ids.length} product(s)`);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update product status. Please try again.");
    }
  };

  const bulkDelete = async (ids) => {
    try {
      const byId = new Map(products.map((p) => [p.id, p]));
      await Promise.all(
        ids.map((id) => {
          const row = byId.get(id);
          if (row?._productLine === "IF" || isInvoiceFinanceProduct(row || id)) {
            return deleteInvoiceFinanceProduct(id);
          }
          return deleteMortgageProduct(id);
        })
      );
      
      // Refresh products list
      await fetchProducts();
      toast.success(`Successfully deleted ${ids.length} product(s)`);
    } catch (error) {
      console.error("Error deleting products:", error);
      toast.error("Failed to delete products. Please try again.");
    }
  };

  const handleCsvImport = async ({ file, meta, upload, imported }) => {
    console.log("CSV import file:", file);
    console.log("CSV import meta:", meta);
    console.log("CSV upload result:", upload);
    console.log("CSV import result:", imported);

    // Refresh products list after successful import
    await fetchProducts();
  };

  const saveIfProduct = async (nextItem) => {
    try {
      const [minRaw, maxRaw] = String(nextItem.range || "")
        .split("-")
        .map((x) => x.trim());
      const minLimit = parseLoanValue(minRaw || "") || nextItem._minLoan;
      const maxLimit = parseLoanValue(maxRaw || "") || nextItem._maxLoan;
      const parsePct = (v) => {
        const n = parseFloat(String(v || "").replace(/[^\d.]/g, ""));
        return Number.isNaN(n) ? null : n;
      };
      const parseMoneyField = (v) => {
        if (v == null || v === "" || v === "—") return null;
        return parseLoanValue(String(v)) || null;
      };
      const statusRaw = String(nextItem.status || "").toLowerCase();
      const termMonths =
        parseInt(String(nextItem.term || nextItem.productTerm || "").replace(/[^\d]/g, ""), 10) ||
        nextItem._termMonths ||
        null;

      const payload = {
        sector: nextItem.sector || nextItem.productName || nextItem.name,
        status: statusRaw.includes("deactiv") ? "Deactivated" : "Active",
        min_limit: minLimit,
        max_limit: maxLimit,
        discount_rate_percent: parsePct(nextItem.discountRate ?? nextItem.rate),
        ip_percent: parsePct(nextItem.ip),
        service_fee_percent: parsePct(nextItem.serviceFee ?? nextItem.prodFee),
        min_service_fee:
          parseMoneyField(nextItem.miniServiceFee) ??
          nextItem._raw?.min_service_fee ??
          null,
        contract_term_months: termMonths,
      };

      await updateInvoiceFinanceProduct(nextItem, payload);
      await fetchProducts();
      toast.success("Invoice Finance product updated successfully");
      setModal({ open: false, mode: "view", product: null });
    } catch (error) {
      console.error("Error updating IF product:", error);
      toast.error(error?.message || "Failed to update Invoice Finance product");
      throw error;
    }
  };

  // Fetch products on mount and when filters change
  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useAdvancedFilter, searchTerm, statusFilter, typeFilter, lenderFilter, rateTypeFilter, showScheduledOnly, minRate, maxRate, minLtv, maxLtv, minLoan, maxLoan, sortBy, sortOrder]);
  
  // Fetch KPI counts / product-line summary metrics
  const [kpiCounts, setKpiCounts] = useState({
    mode: "status",
    total: 0,
    active: 0,
    scheduled: 0,
    deactivated: 0,
    avgDealSize: "—",
    conversionRate: "—",
  });
  
  useEffect(() => {
    const fetchKpiCounts = async () => {
      try {
        const line = String(typeFilter || "BTL").toUpperCase() === "IF" ? "IF" : "BTL";
        const summary = await getProductSummaryByType(line);
        const avgRaw = summary?.average_deal_size?.value ?? summary?.average_deal_size?.current;
        const avgDealSize =
          avgRaw == null || avgRaw === ""
            ? "—"
            : Number(avgRaw).toLocaleString("en-GB", {
                style: "currency",
                currency: "GBP",
                maximumFractionDigits: 0,
              });
        setKpiCounts({
          mode: "line",
          total: Number(summary?.total_products ?? 0),
          active: Number(summary?.active_products ?? 0),
          scheduled: 0,
          deactivated: 0,
          avgDealSize,
          conversionRate: summary?.conversion_rate?.value ?? "—",
        });
      } catch (error) {
        console.error("Error fetching KPI counts:", error);
      }
    };
    
    fetchKpiCounts();
  }, [typeFilter, kpiTick]);

  /* ======= Derived options ======= */
  const lenders = useMemo(
    () => Array.from(new Set(products.map((p) => p.lender))).filter(Boolean).sort(),
    [products]
  );
  const types = useMemo(
    () => Array.from(new Set(products.map((p) => p.type))).filter(Boolean).sort(),
    [products]
  );
  const statuses = useMemo(() => {
    const fromData = Array.from(new Set(products.map((p) => p.status))).filter(Boolean);
    const base =
      typeFilter === "IF"
        ? ["Active", "Deactivated", "Scheduled"]
        : ["Active", "Deactivated", "Pending", "Scheduled"];
    return Array.from(new Set([...base, ...fromData])).sort();
  }, [products, typeFilter]);
  const rateTypes = useMemo(() => {
    const normalized = products.map((p) => {
      const raw = p.productType ?? p._raw?.product_type;
      if (Array.isArray(raw)) {
        return raw
          .map((item) =>
            typeof item === "object" && item != null
              ? String(item.product_type || item.type || item.name || "").trim()
              : String(item || "").trim()
          )
          .filter(Boolean);
      }
      const s = String(raw || "").trim();
      return s && s !== "—" ? [s] : [];
    });
    return Array.from(new Set(normalized.flat())).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [products]);

  /* ======= Filter + Sort ======= */
  // Apply search and all filters client-side so results are accurate regardless of API support
  const filtered = useMemo(() => {
    let list = [...(products || [])];

    const q = (searchTerm || "").trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        // p.productType (and other fields) can be non-strings (objects/numbers) depending on backend shape.
        // Normalize everything to string before calling .toLowerCase().
        const name = String(p.name || p.productName || "").toLowerCase();
        const lender = String(p.lender || "").toLowerCase();
        const type = String(p.type || "").toLowerCase();
        const productType = String(p.productType || "").toLowerCase();
        const id = String(p.id || "").toLowerCase();
        return name.includes(q) || lender.includes(q) || type.includes(q) || productType.includes(q) || id.includes(q);
      });
    }

    if (statusFilter) {
      const want = String(statusFilter).trim().toLowerCase();
      list = list.filter((p) => String(p.status || "").trim().toLowerCase() === want);
    }
    // Type is already scoped by fetch (BTL vs IF); don't re-filter IF rows by
    // facility subtype (Selective / Whole ledger) stored on `type` before mapping.
    if (typeFilter === "BTL") {
      list = list.filter(
        (p) =>
          p._productLine === "BTL" ||
          String(p.type || "").toUpperCase() === "BTL"
      );
    } else if (typeFilter === "IF") {
      list = list.filter(
        (p) => p._productLine === "IF" || String(p.id || "").toUpperCase().startsWith("IF-")
      );
    } else if (typeFilter) {
      list = list.filter((p) => (p.type || "") === typeFilter);
    }
    if (lenderFilter) {
      const lenderLower = (lenderFilter || "").toLowerCase();
      list = list.filter((p) => (p.lender || "").toLowerCase().includes(lenderLower));
    }
    if (typeFilter !== "IF" && rateTypeFilter) {
      const rateLower = (rateTypeFilter || "").toLowerCase();
      list = list.filter((p) => {
        const raw = p.productType ?? p._raw?.product_type;
        if (Array.isArray(raw)) {
          return raw.some((item) => {
            const s =
              typeof item === "object" && item != null
                ? String(item.product_type || item.type || item.name || "")
                : String(item || "");
            return s.toLowerCase().includes(rateLower);
          });
        }
        return String(raw || "").toLowerCase().includes(rateLower);
      });
    }
    if (showScheduledOnly) {
      list = list.filter((p) => p._raw?.product_schedule === 1 || (p.status || "") === "Scheduled");
    }

    // Rate / LTV filters are BTL-only — applying them on IF zeros the list
    if (typeFilter !== "IF") {
      const minRateNum = minRate !== "" && minRate != null ? Number(minRate) : null;
      const maxRateNum = maxRate !== "" && maxRate != null ? Number(maxRate) : null;
      if (minRateNum != null && !Number.isNaN(minRateNum)) {
        list = list.filter((p) => (Number(p.rate) || 0) >= minRateNum);
      }
      if (maxRateNum != null && !Number.isNaN(maxRateNum)) {
        list = list.filter((p) => (Number(p.rate) || 0) <= maxRateNum);
      }

      const minLtvNum = minLtv !== "" && minLtv != null ? Number(minLtv) : null;
      const maxLtvNum = maxLtv !== "" && maxLtv != null ? Number(maxLtv) : null;
      if (minLtvNum != null && !Number.isNaN(minLtvNum)) {
        list = list.filter((p) => (Number(p.ltv) || 0) >= minLtvNum);
      }
      if (maxLtvNum != null && !Number.isNaN(maxLtvNum)) {
        list = list.filter((p) => (Number(p.ltv) || 0) <= maxLtvNum);
      }
    }

    const minLoanNum = minLoan !== "" && minLoan != null ? Number(minLoan) : null;
    const maxLoanNum = maxLoan !== "" && maxLoan != null ? Number(maxLoan) : null;
    if (minLoanNum != null && !Number.isNaN(minLoanNum)) {
      list = list.filter((p) => (Number(p.maxLoan) || 0) >= minLoanNum);
    }
    if (maxLoanNum != null && !Number.isNaN(maxLoanNum)) {
      list = list.filter((p) => (Number(p.minLoan) || 0) <= maxLoanNum);
    }

    const { key, dir } = { key: sortBy || "updatedAt", dir: sortOrder || "desc" };
    const mult = dir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      let aVal = a[key === "rate" ? "rate" : key === "updatedAt" ? "updatedAt" : key === "lender" ? "lender" : key === "type" ? "type" : key === "status" ? "status" : "updatedAt"];
      let bVal = b[key === "rate" ? "rate" : key === "updatedAt" ? "updatedAt" : key === "lender" ? "lender" : key === "type" ? "type" : key === "status" ? "status" : "updatedAt"];
      if (key === "rate") {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
        return mult * (aVal - bVal);
      }
      if (key === "updatedAt") {
        aVal = aVal instanceof Date ? aVal.getTime() : new Date(aVal || 0).getTime();
        bVal = bVal instanceof Date ? bVal.getTime() : new Date(bVal || 0).getTime();
        return mult * (aVal - bVal);
      }
      aVal = String(aVal ?? "").toLowerCase();
      bVal = String(bVal ?? "").toLowerCase();
      return mult * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
    });

    return list;
  }, [
    products,
    searchTerm,
    statusFilter,
    typeFilter,
    lenderFilter,
    rateTypeFilter,
    showScheduledOnly,
    minRate,
    maxRate,
    minLtv,
    maxLtv,
    minLoan,
    maxLoan,
    sortBy,
    sortOrder,
  ]);

  // Effective page size: "All" uses a large number so we show one page
  const effectivePageSize = itemsPerPage >= ALL_PAGE_SIZE ? filtered.length || 1 : itemsPerPage;

  // Pagination - for advanced filter, API handles pagination
  // For simple filter, we do client-side pagination
  const totalPages = useMemo(() => {
    if (useAdvancedFilter) {
      return Math.max(1, Math.ceil((filtered.length || effectivePageSize) / effectivePageSize));
    }
    return Math.max(1, Math.ceil(filtered.length / effectivePageSize));
  }, [filtered.length, useAdvancedFilter, effectivePageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    statusFilter,
    typeFilter,
    lenderFilter,
    rateTypeFilter,
    showScheduledOnly,
    minRate,
    maxRate,
    minLtv,
    maxLtv,
    minLoan,
    maxLoan,
    sortBy,
    sortOrder,
    useAdvancedFilter,
  ]);

  const handleItemsPerPageChange = (value) => {
    const num = value === "all" ? ALL_PAGE_SIZE : Number(value);
    setItemsPerPage(num);
    setCurrentPage(1);
  };

  const paginated = useMemo(() => {
    if (useAdvancedFilter) {
      return filtered;
    }
    const start = (currentPage - 1) * effectivePageSize;
    return filtered.slice(start, start + effectivePageSize);
  }, [filtered, currentPage, useAdvancedFilter, effectivePageSize]);

  const hasActiveFilters = useMemo(() => {
    return (
      !!searchTerm ||
      !!statusFilter ||
      !!lenderFilter ||
      !!rateTypeFilter ||
      !!showScheduledOnly ||
      !!minRate ||
      !!maxRate ||
      !!minLtv ||
      !!maxLtv ||
      !!minLoan ||
      !!maxLoan
    );
  }, [
    searchTerm,
    statusFilter,
    lenderFilter,
    rateTypeFilter,
    showScheduledOnly,
    minRate,
    maxRate,
    minLtv,
    maxLtv,
    minLoan,
    maxLoan,
  ]);

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    // Keep product type (BTL / IF) — management is always type-by-type
    setLenderFilter("");
    setRateTypeFilter("");
    setShowScheduledOnly(false);
    setMinRate("");
    setMaxRate("");
    setMinLtv("");
    setMaxLtv("");
    setMinLoan("");
    setMaxLoan("");
    setAdvancedOpen(false);
  };

  /* ======= KPIs ======= */
  const kpis = useMemo(() => {
    return kpiCounts;
  }, [kpiCounts]);

  /* ======= Selection ======= */
  const toggleAll = () => {
    if (paginated.length > 0 && selectedIds.size === paginated.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginated.map((p) => p.id)));
    }
  };

  const toggleOne = (id, event) => {
    const orderedIds = filtered.map((p) => p.id).filter(Boolean);
    setSelectedIds((prev) => {
      const { selected, anchorId } = applyListSelection(prev, {
        id,
        orderedIds,
        shiftKey: Boolean(event?.shiftKey),
        anchorId: selectionAnchorIdRef.current,
      });
      selectionAnchorIdRef.current = anchorId;
      return selected;
    });
  };

  const selectedList = useMemo(() => Array.from(selectedIds), [selectedIds]);

  const openDeleteConfirm = (ids) => {
    setConfirm({
      title: "Delete product(s)?",
      desc: "This will permanently remove the selected product(s).",
      confirmText: "Delete",
      tone: "red",
      onConfirm: async () => {
        await bulkDelete(ids);
        setSelectedIds(new Set());
        setConfirm(null);
      },
    });
  };

  const openDeactivateConfirm = (ids) => {
    setConfirm({
      title: "Deactivate product(s)?",
      desc: "These products will no longer show to borrowers.",
      confirmText: "Deactivate",
      tone: "red",
      onConfirm: async () => {
        await bulkUpdateStatus(ids, "Deactivated");
        setSelectedIds(new Set());
        setConfirm(null);
      },
    });
  };

  const openActivateConfirm = (ids) => {
    setConfirm({
      title: "Activate product(s)?",
      desc: "These products will be visible to borrowers again.",
      confirmText: "Activate",
      tone: "emerald",
      onConfirm: async () => {
        await bulkUpdateStatus(ids, "Active");
        setSelectedIds(new Set());
        setConfirm(null);
      },
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 px-4 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-sm md:text-base font-semibold text-slate-900">
            Product Management
          </h1>
          <div className="text-xs text-slate-500">
            Manage {typeFilter === "IF" ? "Invoice Finance" : "Buy-to-Let"} products (CSV import + edit). Use Product line to switch type.
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setCsvOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-sky-500 text-white text-xs font-medium hover:bg-sky-600 border border-sky-500"
          >
            <Upload className="h-4 w-4" />
            Import CSV
          </button>
        </div>
      </div>

      {/* KPIs — always type-specific (BTL or IF) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <KpiCardSkeleton key={`kpi-skeleton-${i}`} />
          ))
        ) : (
          <>
            <KpiCard title="Total Products" value={kpis.total} icon={FileText} />
            <KpiCard title="Active Products" value={kpis.active} icon={CheckCircle} tone="emerald" onClick={() => setStatusFilter("Active")} />
            <KpiCard title="Avg Deal Size" value={kpis.avgDealSize} icon={PoundSterling} tone="sky" />
            <KpiCard title="Conversion Rate" value={kpis.conversionRate} icon={Percent} tone="amber" />
          </>
        )}
      </div>

      {/* Toolbar — search + Product line + filters (no horizontal scroll) */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="relative min-w-[160px] flex-1 basis-[12rem]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search product, lender, ID…"
              className="w-full pl-9 pr-3 py-2 h-9 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400"
            />
          </div>

          <Dropdown
            label="Product line"
            value={typeFilter}
            onChange={(line) => {
              const next = line === "IF" ? "IF" : "BTL";
              setTypeFilter(next);
              setSelectedIds(new Set());
              setCurrentPage(1);
              // Clear BTL-only filters so they don't hide the IF list
              setRateTypeFilter("");
              setMinRate("");
              setMaxRate("");
              setMinLtv("");
              setMaxLtv("");
              setStatusFilter("");
              setLenderFilter("");
              setShowScheduledOnly(false);
            }}
            options={PRODUCT_LINE_OPTIONS}
            optionLabels={{ BTL: "BTL", IF: "IF" }}
            allowAll={false}
            icon={Building2}
          />
          <Dropdown label="Status" value={statusFilter} onChange={setStatusFilter} options={statuses} icon={Filter} />
          <Dropdown label="Lender" value={lenderFilter} onChange={setLenderFilter} options={lenders} icon={Building2} />
          {typeFilter !== "IF" ? (
            <Dropdown label="Rate Type" value={rateTypeFilter} onChange={setRateTypeFilter} options={rateTypes.length ? rateTypes : ["Fixed", "Variable", "Tracker"]} icon={Percent} />
          ) : null}

          <Dropdown
            label="Sort"
            value={sortBy}
            onChange={setSortBy}
            options={["updatedAt", "rate", "lender", "type", "status"]}
            icon={ArrowUpDown}
          />

          <button
            type="button"
            onClick={() => setSortOrder((s) => (s === "asc" ? "desc" : "asc"))}
            className="flex items-center justify-center px-2.5 py-2 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
            title="Toggle sort order"
          >
            {sortOrder === "asc" ? "↑" : "↓"}
          </button>

          <button
            type="button"
            onClick={() => setShowScheduledOnly((s) => !s)}
            className={`flex items-center gap-1.5 px-2.5 py-2 text-xs font-medium rounded-lg border transition ${
              showScheduledOnly
                ? "bg-sky-50 text-sky-700 border-sky-200"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
            title="Show only scheduled"
          >
            <CalendarDays className="h-3.5 w-3.5" />
            Scheduled
          </button>

          <button
            type="button"
            onClick={fetchProducts}
            className="flex items-center gap-1.5 px-2.5 py-2 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
            title="Refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>

          {hasActiveFilters ? (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-red-50 border border-red-200 text-xs font-medium text-red-700 hover:bg-red-100 transition"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          ) : null}
        </div>

        {/* Advanced Filters (hidden until clicked) */}
        <div className="rounded-2xl border border-slate-200 overflow-hidden">
          <button
            type="button"
            onClick={() => {
              setAdvancedOpen((s) => !s);
              // Auto-enable advanced filter when opening advanced filters section
              if (!advancedOpen && (minRate || maxRate || minLtv || maxLtv || minLoan || maxLoan)) {
                setUseAdvancedFilter(true);
              }
            }}
            className="w-full px-4 py-3 bg-slate-50 flex items-center justify-between hover:bg-slate-100 transition"
          >
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
              <SlidersHorizontal className="h-4 w-4" />
              Advanced Filters
              <span className="text-[11px] font-medium text-slate-500">
                {advancedOpen ? "(click to hide)" : "(click to show)"}
              </span>
              {useAdvancedFilter && (
                <span className="ml-2 px-2 py-0.5 bg-sky-100 text-sky-700 rounded-full text-[10px] font-medium">
                  API Mode
                </span>
              )}
            </div>
            <ChevronDown className={`h-4 w-4 text-slate-500 transition ${advancedOpen ? "rotate-180" : ""}`} />
          </button>

          {advancedOpen ? (
            <div className="p-4 bg-white">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-xs text-slate-600">
                  Advanced filters use server-side API for better performance
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={useAdvancedFilter}
                    onChange={(e) => setUseAdvancedFilter(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <span className="text-slate-700">Use Advanced API</span>
                </label>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                <MiniInput label="Min Rate" value={minRate} onChange={setMinRate} placeholder="e.g. 2.00" />
                <MiniInput label="Max Rate" value={maxRate} onChange={setMaxRate} placeholder="e.g. 6.00" />
                <MiniInput label="Min LTV" value={minLtv} onChange={setMinLtv} placeholder="e.g. 60" />
                <MiniInput label="Max LTV" value={maxLtv} onChange={setMaxLtv} placeholder="e.g. 80" />
                <MiniInput label="Min Loan" value={minLoan} onChange={setMinLoan} placeholder="e.g. 25000" />
                <MiniInput label="Max Loan" value={maxLoan} onChange={setMaxLoan} placeholder="e.g. 2000000" />
              </div>
            </div>
          ) : null}
        </div>

        {/* Bulk actions */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div className="text-xs text-slate-500">
            Showing {filtered.length ? (currentPage - 1) * itemsPerPage + 1 : 0}–
            {Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length} (from{" "}
            {products.length} total)
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-white">
              <CheckSquare className="h-4 w-4 text-slate-500" />
              <div className="text-xs text-slate-700">
                Selected: <span className="font-semibold">{selectedIds.size}</span>
              </div>
            </div>

            <button
              disabled={!selectedIds.size}
              onClick={() => openActivateConfirm(selectedList)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle className="h-3.5 w-3.5" />
              Activate
            </button>

            <button
              disabled={!selectedIds.size}
              onClick={() => openDeactivateConfirm(selectedList)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs font-medium text-red-700 hover:bg-red-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Ban className="h-3.5 w-3.5" />
              Deactivate
            </button>

            <button
              disabled={!selectedIds.size}
              onClick={() => openDeleteConfirm(selectedList)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-xs font-medium text-rose-700 hover:bg-rose-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
<div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500">
        <tr>
          <th className="px-4 py-3 text-left">
            <input
              type="checkbox"
              checked={paginated.length > 0 && selectedIds.size === paginated.length}
              onChange={toggleAll}
              className="rounded border-slate-300 text-sky-500 focus:ring-sky-400"
            />
          </th>
          <th className="px-4 py-3 text-left font-medium">Product</th>
          {typeFilter !== "IF" && <th className="px-4 py-3 text-left font-medium">Category</th>}
          <th className="px-4 py-3 text-left font-medium">Lender</th>
          <th className="px-4 py-3 text-center font-medium">
            {typeFilter === "IF" ? "Discount Rate" : "Rate"}
          </th>
          <th className="px-4 py-3 text-center font-medium">
            {typeFilter === "IF" ? "Service Fee" : "Arrangement Fee"}
          </th>
          <th className="px-4 py-3 text-left font-medium">
            {typeFilter === "IF" ? "Contract Term" : "Term"}
          </th>
          <th className="px-4 py-3 text-left font-medium">Amount Range</th>
          <th className="px-4 py-3 text-left font-medium">Status</th>
          <th className="px-4 py-3 text-left font-medium">Actions</th>
        </tr>
      </thead>

      <tbody>
        {loading ? (
          <TableSkeleton rows={10} cols={typeFilter === "IF" ? 8 : 9} />
        ) : paginated.length === 0 ? (
          <tr>
            <td colSpan={typeFilter === "IF" ? 9 : 10} className="px-6 py-10 text-center text-sm text-slate-500">
              No products found.
            </td>
          </tr>
        ) : (
          paginated.map((p) => (
            <tr
              key={p.id}
              className="border-b border-slate-100 last:border-0 hover:bg-slate-50/40"
            >
              {/* Checkbox */}
              <td className="px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedIds.has(p.id)}
                  onMouseDown={(e) => {
                    if (e.shiftKey) e.preventDefault();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleOne(p.id, e);
                  }}
                  onChange={() => {}}
                  className="rounded border-slate-300 text-sky-500 focus:ring-sky-400"
                />
              </td>

              {/* Product */}
              <td className="px-4 py-3">
                <div className="text-sm font-semibold text-slate-900">
                  {p.name || p.productName || p.id}
                </div>
                <div className="text-xs text-sky-600 font-medium">
                  {p.id}
                </div>
              </td>

              {typeFilter !== "IF" && (
                <td className="px-4 py-3">
                  <TypeBadge t={p.type || "—"} />
                </td>
              )}

              {/* Lender (BTL: email local part, e.g. paragon@gmail.com → paragon; IF: lender name from owner) */}
              <td className="px-4 py-3">
                <span className="text-sm font-medium text-slate-800">
                  {p.lender ?? "—"}
                </span>
              </td>

              {/* Rate / Discount Rate */}
              <td className="px-4 py-3 text-center">
                {p._productLine === "IF" ? (
                  <>
                    <div className="text-sm font-semibold text-slate-900">
                      {p.discountRate || pct(p.rate)}
                    </div>
                    <div className="text-xs text-sky-600 font-medium">
                      IP: {p.ip || "—"}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-sm font-semibold text-slate-900">{pct(p.rate)}</div>
                    <div className="text-xs text-sky-600 font-medium">APRC: {pct(p.aprc)}</div>
                  </>
                )}
              </td>

              {/* Arrangement Fee / Service Fee */}
              <td className="px-4 py-3 text-center">
                <div className="text-sm font-medium text-slate-900">
                  {p._productLine === "IF"
                    ? p.serviceFee || pct(p.prodFee)
                    : p.productFeeMode === "percent"
                    ? pct(p.prodFee)
                    : money(p.prodFee)}
                </div>
              </td>

              {/* Term */}
              <td className="px-4 py-3">
                {p._productLine === "IF" ? (
                  <>
                    <div className="text-sm font-medium text-slate-900">
                      {p.productTerm || p.term || "—"} months
                    </div>
                    {p.productType && p.productType !== "—" ? (
                      <div className="text-xs text-sky-600 font-medium">{p.productType}</div>
                    ) : null}
                  </>
                ) : (
                  <>
                    <div className="text-sm font-medium text-slate-900">
                      {p.productTerm} {p.productTerm === "1" ? "Year" : "Years"}
                    </div>
                    <div className="text-xs text-sky-600 font-medium">{p.productType}</div>
                  </>
                )}
              </td>

              {/* Amount Range */}
              <td className="px-4 py-3">
                <div className="text-sm font-medium text-slate-900">
                  {p.range || `${money(p.minLoan)} - ${money(p.maxLoan)}`}
                </div>
                {p._productLine !== "IF" ? (
                  <div className="text-xs text-sky-600 font-medium">LTV: {p.ltv}%</div>
                ) : null}
              </td>

              {/* Status */}
              <td className="px-4 py-3">
                <StatusPill status={p.status} />
              </td>

              {/* Actions */}
              <td className="px-4 py-3">
                <div className="flex items-center gap-3 text-slate-700">
                  <button
                    onClick={() => setModal({ open: true, mode: "view", product: p })}
                    className="hover:text-sky-600"
                    title="View"
                  >
                    <Eye className="h-4 w-4" />
                  </button>

                  <button
                    onClick={() => setModal({ open: true, mode: "edit", product: p })}
                    className="hover:text-sky-600"
                    title="Edit"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>

                  <button
                    onClick={() =>
                      setConfirm({
                        title: p.status === "Deactivated" ? "Activate product?" : "Deactivate product?",
                        desc:
                          p.status === "Deactivated"
                            ? "This product will become visible to borrowers again."
                            : "This product will be hidden from borrowers.",
                        confirmText: p.status === "Deactivated" ? "Activate" : "Deactivate",
                        tone: p.status === "Deactivated" ? "emerald" : "red",
                        onConfirm: async () => {
                          await bulkUpdateStatus([p.id], p.status === "Deactivated" ? "Active" : "Deactivated");
                          setConfirm(null);
                        },
                      })
                    }
                    className="hover:text-red-600"
                    title={p.status === "Deactivated" ? "Activate" : "Deactivate"}
                  >
                    <Ban className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
</div>

      {/* Pagination at the bottom */}
      <Pagination
        currentPage={currentPage}
        totalPages={Math.max(1, totalPages)}
        itemsPerPage={itemsPerPage}
        totalCount={filtered.length}
        onPage={(p) => setCurrentPage(p)}
        onItemsPerPageChange={handleItemsPerPageChange}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        allPageSize={ALL_PAGE_SIZE}
      />

      {/* Product Modal */}
      {modal.open && modal.product?._productLine === "IF" && modal.mode === "view" ? (
        <ViewProductModal
          item={modal.product}
          productLine="if"
          onClose={() => setModal({ open: false, mode: "view", product: null })}
        />
      ) : null}
      {modal.open && modal.product?._productLine === "IF" && modal.mode === "edit" ? (
        <EditProductModal
          item={modal.product}
          productLine="if"
          onSave={saveIfProduct}
          onClose={() => setModal({ open: false, mode: "view", product: null })}
        />
      ) : null}
      {modal.open && modal.product?._productLine !== "IF" ? (
        <ProductModal
          mode={modal.mode}
          product={modal.product}
          onClose={() => setModal({ open: false, mode: "view", product: null })}
          onSave={async (p) => {
            try {
              const result = await upsertProduct(p);
              // Show confirmation modal after successful update
              if (result?.success) {
                setConfirm({
                  title: "Product Updated Successfully",
                  description: `Product "${result.productName || p.productName || p.name}" has been updated successfully.`,
                  confirmText: "OK",
                  tone: "emerald",
                  onConfirm: () => {
                    setConfirm(null);
                    setModal({ open: false, mode: "view", product: null });
                  },
                });
              }
              return result;
            } catch (error) {
              // Error is already handled in upsertProduct
              throw error;
            }
          }}
          lenders={lenders}
          products={products}
        />
      ) : null}

      {/* CSV Import Modal */}
      {csvOpen ? (
        <CsvImportModal 
          onClose={() => setCsvOpen(false)} 
          onImport={handleCsvImport}
          lenders={lenders}
          products={products}
          defaultProductLine={typeFilter === "IF" ? "IF" : "BTL"}
        />
      ) : null}

      {/* Confirm */}
      {confirm ? (
        <ConfirmModal
          title={confirm.title}
          description={confirm.description || confirm.desc}
          confirmText={confirm.confirmText}
          tone={confirm.tone}
          onClose={() => setConfirm(null)}
          onConfirm={confirm.onConfirm}
        />
      ) : null}
    </div>
  );
}
